
import { useState, useMemo, useEffect, useRef, useCallback, Fragment, use } from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, ComposedChart,
  ScatterChart, Scatter, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, LabelList, ZAxis,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import DDRDashboard, { isDDRWorkbook, parseDDRWorkbook } from "./DDRDashboard";
export { isDDRWorkbook, parseDDRWorkbook };
export const DASH_T = {
  bg: "#FAFAFA", surface: "#FFFFFF", surface2: "#FFF5F5",
  border: "#FADDDD", borderMed: "#F5BABA", borderStrong: "#E87878",
  accent: "#E8001A", accentDark: "#B50015", accentDeep: "#800010",
  accentSoft: "#FFF0F0", accentMid: "#FFD6D6",
  text: "#1A0005", textMid: "#5C1020", textDim: "#9A5060", textLight: "#C07080",
  pos: "#1A7A3A", neg: "#C42020", warn: "#B06000", neutral: "#8A7070",
  bgAlt: "#FFF8F8",
  shadow: "0 2px 8px rgba(232,0,26,0.08)",
  shadowMd: "0 4px 16px rgba(232,0,26,0.12)",
};
const T = DASH_T;

const REGION_COLOR = {
  "Coastal East & Upper South": T.accent,
  "Lower South": "#C42020",
  "West": "#8A0015",
};
const CAT_COLOR = { Sparkling: T.accent, Water: "#1A7A9A", Juice: "#E06010", "All Others": "#7A3A8A" };
const CATEGORIES = ["Sparkling", "Water", "Juice", "All Others"];
const REGIONS    = ["Coastal East & Upper South", "Lower South", "West"];
const API_BASE   = (process.env.REACT_APP_BACKEND || "http://localhost:8000");

// ── Fetch helpers ──────────────────────────────────────────────────────────
async function fetchDashboardSpec(sessionId, refresh = false, extraParams = "") {
  const token = localStorage.getItem("konet_token");
  const url   = `${API_BASE}/rag/dashboard/generate/${sessionId}?refresh=${refresh}${extraParams}`;
  const res   = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { const e = await res.text(); throw new Error(`API ${res.status}: ${e.slice(0,200)}`); }
  return res.json();
}
async function saveDashboardVersion(sessionId, spec, label) {
  const token = localStorage.getItem("konet_token");
  try {
    await fetch(`${API_BASE}/rag/dashboard/versions/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ session_id: sessionId, spec, version_label: label }),
    });
  } catch (e) { console.warn("Version save failed:", e); }
}
async function fetchVersionList(sessionId) {
  const token = localStorage.getItem("konet_token");
  const res = await fetch(`${API_BASE}/rag/dashboard/versions/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return [];
  const d = await res.json(); return d.versions || [];
}
async function fetchVersionSpec(sessionId, vNum) {
  const token = localStorage.getItem("konet_token");
  const res = await fetch(`${API_BASE}/rag/dashboard/versions/${sessionId}/${vNum}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("Version not found");
  return res.json();
}

// ── Math helpers ───────────────────────────────────────────────────────────
const fmtPct     = (v, d=1) => v==null||isNaN(v) ? "—" : `${(v*100).toFixed(d)}%`;
const fmtPctSign = (v, d=1) => v==null||isNaN(v) ? "—" : `${v>=0?"+":""}${(v*100).toFixed(d)}%`;
const fmtVolMn   = (v) => {
  if (v==null||isNaN(v)) return "—";
  const mn = v/1000;
  if (mn>=100) return `${mn.toFixed(0)} Mn UC`;
  if (mn>=10)  return `${mn.toFixed(1)} Mn UC`;
  return `${mn.toFixed(2)} Mn UC`;
};
const fmtVolMnShort = (v) => {
  if (v==null||isNaN(v)) return "—";
  const mn=v/1000;
  if (mn>=100) return `${mn.toFixed(0)}Mn`;
  if (mn>=10)  return `${mn.toFixed(1)}Mn`;
  return `${mn.toFixed(2)}Mn`;
};
const growthColor = (v) => v==null ? T.neutral : v>0.0005 ? T.pos : v<-0.0005 ? T.neg : T.neutral;
const aggGrowth = (rows, key) => {
  let c=0, p=0;
  rows.forEach(r=>{ const g=r[key]; if(g==null||isNaN(g)||!r.vol)return; c+=r.vol; p+=r.vol/(1+g); });
  return p>0 ? c/p-1 : 0;
};
const agg2YCAGR = (rows, key) => {
  let c=0, p=0;
  rows.forEach(r=>{ const g=r[key]; if(g==null||isNaN(g)||!r.vol)return; c+=r.vol; p+=r.vol/Math.pow(1+g,2); });
  return p>0 ? Math.pow(c/p,0.5)-1 : 0;
};
const aggCycling = (rows, grKey, cycKey) => {
  let py=0, ppy=0;
  rows.forEach(r=>{ const g=r[grKey],c=r[cycKey]; if(g==null||c==null||!r.vol)return; const pyV=r.vol/(1+g); py+=pyV; ppy+=pyV/(1+c); });
  return ppy>0 ? py/ppy-1 : 0;
};
const heatColor = (v) => {
  if (v==null||isNaN(v)) return "#FFF0F0";
  const t = Math.max(-1,Math.min(1,v/0.25));
  return t>=0 ? `rgba(26,122,58,${0.1+0.6*t})` : `rgba(196,32,32,${0.1+0.6*Math.abs(t)})`;
};
function evalExpr(expr, rows) {
  try { return new Function("rows",`return (${expr})`)(rows); } catch { return "—"; }
}
function fmtValue(val, format) {
  if (val==null||val==="—") return "—";
  const n=Number(val);
  switch(format) {
    case "percent":  return isNaN(n)?val:`${n.toFixed(1)}%`;
    case "currency": return isNaN(n)?val:`$${n.toLocaleString(undefined,{maximumFractionDigits:2})}`;
    case "number":   return isNaN(n)?val:n.toLocaleString(undefined,{maximumFractionDigits:2});
    default:         return String(val);
  }
}

// ── Workbook parsers (exported for RAG.js) ─────────────────────────────────
export function isHCCBWorkbook(workbook) {
  return workbook.SheetNames.some(n => /vol\s*ytd\s*growth/i.test(n));
}

export function parseHCCBWorkbook(workbook, XLSX) {
  const out = { cboData:[], catData:[], months:[], momData:{}, momCat:{}, totals:{}, isHCCB:true };
  const s1Name = workbook.SheetNames.find(n=>/vol\s*ytd\s*growth\s*$/i.test(n))||workbook.SheetNames[0];
  const s1 = workbook.Sheets[s1Name];
  if (!s1) return null;
  const s1Rows = XLSX.utils.sheet_to_json(s1,{header:1,raw:true});
  for (let i=1;i<s1Rows.length;i++) {
    const r=s1Rows[i], l3=r[2], l1=r[0];
    if (typeof l1==="string"&&l1.includes("HCCB+")&&!r[2]) { out.totals.hccb={vol:r[3],volMix:r[4],ytdCyc:r[5],cagr2y:r[6],ytdGr:r[7],qtdGr:r[8],mtdGr:r[9]}; continue; }
    if (typeof l3!=="string"||!l3.trim()) continue;
    out.cboData.push({ l3:l3.replace(/^CBO\s*/i,"").trim(),l2:r[1],vol:r[3],volMix:r[4],ytdCyc:r[5],cagr2y:r[6],ytdGr:r[7],qtdGr:r[8],mtdGr:r[9],mtdCyc:r[10],mtd2y:r[11] });
  }
  const s2Name=workbook.SheetNames.find(n=>/vol.*ytd.*growth.*categ/i.test(n));
  if (s2Name) {
    const s2Rows=XLSX.utils.sheet_to_json(workbook.Sheets[s2Name],{header:1,raw:true});
    for (let i=1;i<s2Rows.length;i++) {
      const r=s2Rows[i],l3=r[0],cat=r[1];
      if (typeof cat!=="string"||!cat.trim()||typeof l3!=="string"||!l3.trim()) continue;
      out.catData.push({l3:l3.replace(/^CBO\s*/i,"").trim(),cat:cat.trim(),vol:r[2],volMix:r[3],ytdCyc:r[4],cagr2y:r[5],ytdGr:r[6],qtdGr:r[7],mtdGr:r[8]});
    }
  }
  const s3Name=workbook.SheetNames.find(n=>/^mom\s*vol\s*growth$/i.test(n))||workbook.SheetNames.find(n=>/mom.*vol.*growth$/i.test(n));
  if (s3Name) {
    const s3Rows=XLSX.utils.sheet_to_json(workbook.Sheets[s3Name],{header:1,raw:true});
    let hi=s3Rows.findIndex(r=>r&&r.some(x=>typeof x==="string"&&/L1_FL_INDIA/i.test(x)));
    if (hi<0) hi=2;
    const yearsRow=s3Rows[hi-1]||[], headerRow=s3Rows[hi], monthCols=headerRow.slice(3);
    out.months=monthCols.map((m,i)=>{ const y=yearsRow[i+3]; return y?`${String(m).trim()} ${y}`:String(m).trim(); });
    for (let i=hi+1;i<s3Rows.length;i++) {
      const r=s3Rows[i]; if (!r||!r.length) continue;
      const l1=r[0],l3=r[2]; let key=null;
      if (typeof l3==="string"&&l3.trim()) key=l3.replace(/^CBO\s*/i,"").trim();
      else if (typeof l1==="string"&&/HCCB\+\s*Total/i.test(l1)) key="HCCB+ Total";
      else continue;
      out.momData[key]=r.slice(3,3+monthCols.length).map(v=>typeof v==="number"?v:null);
    }
  }
  const s4Name=workbook.SheetNames.find(n=>/mom.*vol.*growth.*categ/i.test(n));
  if (s4Name) {
    const s4Rows=XLSX.utils.sheet_to_json(workbook.Sheets[s4Name],{header:1,raw:true});
    let h4=s4Rows.findIndex(r=>r&&r.some(x=>typeof x==="string"&&/L2_RD_CD/i.test(x)));
    if (h4<0) h4=1;
    for (let i=h4+1;i<s4Rows.length;i++) {
      const r=s4Rows[i]; if (!r) continue;
      const l3=r[1],cat=r[2];
      if (typeof cat!=="string"||!cat.trim()||typeof l3!=="string"||!l3.trim()) continue;
      const key=l3.replace(/^CBO\s*/i,"").trim();
      if (!out.momCat[key]) out.momCat[key]={};
      out.momCat[key][cat.trim()]=r.slice(3,3+out.months.length).map(v=>typeof v==="number"?v:null);
    }
  }
  return out;
}

export function parseGenericWorkbook(workbook, XLSX) {
  const allSheets=workbook.SheetNames; let best=null;
  for (const sheetName of allSheets) {
    const sheet=workbook.Sheets[sheetName]; if (!sheet) continue;
    const raw=XLSX.utils.sheet_to_json(sheet,{header:1,raw:true,defval:null}); if (!raw.length) continue;
    let headerIdx=0,bestScore=-1;
    for (let i=0;i<Math.min(10,raw.length);i++) {
      const row=raw[i]||[],sc=row.filter(c=>typeof c==="string"&&c.trim()).length,tot=row.filter(c=>c!==null&&c!==undefined&&c!=="").length;
      const score=sc*2-(tot-sc);
      if (sc>=2&&score>bestScore){bestScore=score;headerIdx=i;}
    }
    const headers=(raw[headerIdx]||[]).map((h,i)=>h!=null&&String(h).trim()?String(h).trim():`col_${i}`);
    const dataRows=raw.slice(headerIdx+1).filter(r=>r&&r.some(c=>c!==null&&c!==undefined&&c!==""));
    if (!dataRows.length) continue;
    const objRows=dataRows.map(r=>{ const o={}; headers.forEach((h,i)=>{o[h]=r[i]??null;}); return o; });
    const score=objRows.length*headers.length;
    if (!best||score>best.score) best={sheetName,headers,rows:objRows,headerIdx,score};
  }
  if (!best||!best.rows.length) return null;
  const {headers:cols,rows}=best;
  const colTypes={};
  cols.forEach(c=>{
    const vals=rows.slice(0,80).map(r=>r[c]).filter(v=>v!==null&&v!==undefined&&v!=="");
    if (!vals.length){colTypes[c]="empty";return;}
    const numVals=vals.filter(v=>typeof v==="number"||(typeof v==="string"&&v.trim()!==""&&!isNaN(Number(v))));
    if (numVals.length<vals.length*0.6){colTypes[c]="category";return;}
    const nums=numVals.map(v=>Number(v)),mn=Math.min(...nums),mx=Math.max(...nums);
    colTypes[c]=(mn>=-2&&mx<=5&&(mx-mn)<10)?"ratio":"numeric";
  });
  const catCols=cols.filter(c=>colTypes[c]==="category");
  const numCols=cols.filter(c=>colTypes[c]==="numeric");
  const ratioCols=cols.filter(c=>colTypes[c]==="ratio");
  const monthRe=/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|q[1-4]|fy|20\d{2}|19\d{2})/i;
  const tsCols=cols.filter(c=>monthRe.test(String(c).trim())&&(colTypes[c]==="numeric"||colTypes[c]==="ratio"));
  let segCol=catCols.find(c=>{ const u=new Set(rows.map(r=>String(r[c]??""))); return u.size===rows.length||u.size>=Math.min(rows.length,3); })||catCols[0]||cols[0];
  let groupCol=catCols.find(c=>c!==segCol)||null;
  const volCol=numCols.length>0?numCols.reduce((a,b)=>{ const aM=Math.max(...rows.slice(0,20).map(r=>Number(r[a])||0)),bM=Math.max(...rows.slice(0,20).map(r=>Number(r[b])||0)); return bM>aM?b:a; }):(ratioCols[0]||cols[1]||cols[0]);
  const gr1=ratioCols[0]||numCols.find(c=>c!==volCol), gr2=ratioCols[1]||null, gr3=ratioCols[2]||null, gr4=ratioCols[3]||null;
  const cboData=rows.map(r=>({ l3:String(r[segCol]??"").trim()||('"unnamed"'), l2:groupCol?String(r[groupCol]??"Group").trim():"All", vol:typeof r[volCol]==="number"?r[volCol]:(Number(r[volCol])||0), volMix:0, ytdGr:gr1?(typeof r[gr1]==="number"?r[gr1]:Number(r[gr1])||0):0, qtdGr:gr2?(typeof r[gr2]==="number"?r[gr2]:Number(r[gr2])||0):0, mtdGr:gr3?(typeof r[gr3]==="number"?r[gr3]:Number(r[gr3])||0):0, cagr2y:gr4?(typeof r[gr4]==="number"?r[gr4]:Number(r[gr4])||0):0, ytdCyc:0 })).filter(r=>r.l3&&r.l3!=='"unnamed"');
  const dedup={};
  cboData.forEach(c=>{ if (!dedup[c.l3]) dedup[c.l3]={...c}; else dedup[c.l3].vol+=c.vol; });
  const finalCboData=Object.values(dedup);
  const totalVol=finalCboData.reduce((s,r)=>s+(r.vol||0),0)||1;
  finalCboData.forEach(r=>{r.volMix=r.vol/totalVol;});
  let months=[]; const momData={};
  if (tsCols.length>=2) {
    months=tsCols.map(String);
    finalCboData.forEach(c=>{ const origRow=rows.find(r=>String(r[segCol]??"").trim()===c.l3); if (!origRow)return; momData[c.l3]=tsCols.map(m=>{ const v=origRow[m]; return typeof v==="number"?v:(v!==null&&v!==undefined&&!isNaN(Number(v))?Number(v):null); }); });
  }
  return { cboData:finalCboData, catData:[], months, momData, momCat:{}, totals:{}, isHCCB:false,
    columnLabels:{ vol:String(volCol), ytdGr:gr1?String(gr1):"Growth 1", qtdGr:gr2?String(gr2):"Growth 2", mtdGr:gr3?String(gr3):"Growth 3", cagr2y:gr4?String(gr4):"Growth 4", segCol:String(segCol), groupCol:groupCol?String(groupCol):null, allNumeric:[...numCols,...ratioCols].map(String), allCat:catCols.map(String), sheetNames:allSheets, activeSheet:best.sheetName, isTimeSeries:tsCols.length>=2, allColumns:cols } };
}
// ── Shared UI atoms ────────────────────────────────────────────────────────
const GlassTip = ({ active, payload, label }) => {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:"rgba(255,255,255,0.97)",border:`1px solid ${T.borderMed}`,borderRadius:8,padding:"10px 14px",fontSize:11,color:T.text,boxShadow:T.shadowMd}}>
      {label&&<div style={{color:T.textDim,marginBottom:6,fontSize:10,textTransform:"uppercase",letterSpacing:0.5}}>{label}</div>}
      {payload.map((p,i)=>(
        <div key={i} style={{display:"flex",justifyContent:"space-between",gap:16,alignItems:"center"}}>
          <span style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{width:8,height:8,borderRadius:2,background:p.color||p.fill}}/>
            <span style={{color:T.textMid}}>{p.name}</span>
          </span>
          <span style={{color:T.text,fontWeight:600}}>
            {typeof p.value==="number"&&Math.abs(p.value)<=5?fmtPctSign(p.value):p.value?.toLocaleString?.()??p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

function Sparkline({ data, color, height=28 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={(data||[]).map((v,i)=>({i,v}))}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false}/>
      </LineChart>
    </ResponsiveContainer>
  );
}

function KPICard({ label, value, delta, color, sparkData, sparkColor, subtitle, icon }) {
  return (
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:16,position:"relative",overflow:"hidden",minHeight:110,boxShadow:T.shadow,borderTop:`3px solid ${color||T.accent}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{color:T.textDim,fontSize:10,letterSpacing:1.2,textTransform:"uppercase",fontWeight:600}}>{label}</div>
          <div style={{color:T.text,fontSize:22,fontWeight:700,marginTop:6,letterSpacing:-0.5}}>{value}</div>
          {subtitle&&<div style={{color:T.textDim,fontSize:10,marginTop:2}}>{subtitle}</div>}
        </div>
        {icon&&<span style={{fontSize:24,opacity:0.25}}>{icon}</span>}
      </div>
      {(delta!==undefined||sparkData)&&(
        <div style={{marginTop:10,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          {delta!==undefined&&<div style={{color:growthColor(delta),fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:4,background:delta>=0?"rgba(26,122,58,0.12)":"rgba(196,32,32,0.1)"}}>{delta>=0?"▲":"▼"} {fmtPctSign(Math.abs(delta))}</div>}
          {sparkData&&<div style={{flex:1,marginLeft:10,opacity:0.8}}><Sparkline data={sparkData} color={sparkColor||color||T.accent}/></div>}
        </div>
      )}
    </div>
  );
}

function Card({ title, subtitle, badge, children, style: extra={} }) {
  return (
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:16,display:"flex",flexDirection:"column",boxShadow:T.shadow,...extra}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:14}}>
        <div>
          <div style={{color:T.text,fontSize:15,fontWeight:700,letterSpacing:-0.2}}>{title}</div>
          {subtitle&&<div style={{color:T.textDim,fontSize:10.5,marginTop:2}}>{subtitle}</div>}
        </div>
        {badge&&<div style={{background:T.accentSoft,border:`1px solid ${T.borderMed}`,color:T.accent,fontSize:9,padding:"3px 8px",borderRadius:4,fontWeight:700,letterSpacing:0.5}}>{badge}</div>}
      </div>
      <div style={{flex:1,minHeight:0}}>{children}</div>
    </div>
  );
}

// ── Version History Dropdown ───────────────────────────────────────────────
function VersionHistoryDropdown({ specHistory, currentSpec, onRestore, sessionId }) {
  const [open,setOpen]           = useState(false);
  const [savedVersions,setSaved] = useState([]);
  const [loadingList,setLoadList]= useState(false);
  const [loadingSpec,setLoadSpec]= useState(null);
  const [currentVersion,setCurrentVersion] = useState(null);
  const ref = useRef(null);

  useEffect(()=>{
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",h);
    return ()=>document.removeEventListener("mousedown",h);
  },[]);

useEffect(() => {
  if (!open || !sessionId) return;
  setLoadList(true);
  fetchVersionList(sessionId)
   .then(versions => {
  setSaved(prev => {
    // Only update currentVersion if a new version was added
    if (versions.length > prev.length && versions.length > 0) {
      setCurrentVersion(versions[versions.length - 1].version);
    }
    return versions;
  });
})
    .catch(() => {})
    .finally(() => setLoadList(false));
}, [open, sessionId]);

 
  const loadVersion = async (vNum) => {
    setLoadSpec(vNum);
    setCurrentVersion(vNum);
    try { const d=await fetchVersionSpec(sessionId,vNum); if(d.spec){onRestore(d.spec,true);setOpen(false);} }
    catch(e){ console.error(e); }
    finally { setLoadSpec(null); }
  };

  const totalVersions  = savedVersions.length||(specHistory.length+1);


  return (
    <div ref={ref} style={{position:"relative"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{background:T.surface,border:`1px solid ${T.border}`,color:T.textMid,padding:"6px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
        <span>🕐</span> v{currentVersion}
        <span style={{background:T.accentSoft,color:T.accent,padding:"1px 6px",borderRadius:8,fontSize:9,fontWeight:700}}>{totalVersions} {totalVersions===1?"version":"versions"}</span>
        <span style={{fontSize:10,color:T.textDim}}>{open?"▲":"▼"}</span>
      </button>
      {open&&(
        <div style={{position:"absolute",top:"calc(100% + 6px)",right:0,background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",zIndex:300,minWidth:270,overflow:"hidden",animation:"dashFadeSlide 0.15s ease"}}>
          <div style={{padding:"10px 14px",borderBottom:`1px solid ${T.border}`,background:T.bgAlt,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:T.text}}>Dashboard Versions</div>
              <div style={{fontSize:10,color:T.textDim,marginTop:2}}>☁️ Saved to cloud · persists after refresh</div>
            </div>
            {loadingList&&<div style={{width:14,height:14,border:`2px solid ${T.border}`,borderTopColor:T.accent,borderRadius:"50%",animation:"dashSpin 0.8s linear infinite"}}/>}
          </div>
          <div style={{maxHeight:380,overflowY:"auto"}}>
            {loadingList?(
              <div style={{padding:20,textAlign:"center",color:T.textDim,fontSize:12}}>Loading versions…</div>
            ):savedVersions.length===0?(
              <div style={{padding:20,textAlign:"center",color:T.textDim,fontSize:12,fontStyle:"italic",lineHeight:1.6}}>No saved versions yet.<br/><span style={{fontSize:11}}>Versions save automatically when you edit.</span></div>
            ):(
              [...savedVersions].reverse().map((v,i)=>{
              const isCurrent=v.version===currentVersion, isLoading=loadingSpec===v.version;
                return (
                  <div key={v.version} onClick={()=>!isCurrent&&!isLoading&&loadVersion(v.version)}
                    style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:10,background:isCurrent?T.accentSoft:"transparent",borderBottom:`1px solid ${T.border}`,cursor:isCurrent?"default":"pointer",transition:"background 0.12s"}}
                    onMouseEnter={e=>{if(!isCurrent)e.currentTarget.style.background=T.bgAlt;}}
                    onMouseLeave={e=>{if(!isCurrent)e.currentTarget.style.background="transparent";}}>
                    <div style={{width:32,height:32,borderRadius:6,flexShrink:0,background:isCurrent?T.accent:T.bgAlt,border:isCurrent?"none":`1px solid ${T.border}`,color:isCurrent?"#fff":T.textMid,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800}}>
                      {isLoading?<div style={{width:12,height:12,border:`2px solid ${T.border}`,borderTopColor:T.accent,borderRadius:"50%",animation:"dashSpin 0.8s linear infinite"}}/>:`v${v.version}`}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:isCurrent?T.accent:T.text,display:"flex",alignItems:"center",gap:6}}>
                        {v.version}
                        {isCurrent&&<span style={{fontSize:9,padding:"1px 6px",background:T.accent,color:"#fff",borderRadius:6,fontWeight:700}}>CURRENT</span>}
                      </div>
                      <div style={{fontSize:10,color:T.textDim,marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.title}</div>
                      <div style={{fontSize:9,color:T.textDim,marginTop:2,fontFamily:"monospace"}}>
                        {v.kpi_count} KPIs · {v.chart_count} charts · {v.table_count} tables{" · "}
                        {new Date(v.saved_at).toLocaleString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                      </div>
                    </div>
                    <div style={{fontSize:10,color:T.accent,fontWeight:600,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.border}`,background:T.surface,flexShrink:0}}>{isLoading?"…":"Restore"}</div>
                  </div>
                );
              })
            )}
          </div>
          <div style={{padding:"8px 14px",borderTop:`1px solid ${T.border}`,background:T.bgAlt,fontSize:10,color:T.textDim,fontStyle:"italic"}}>Click any version to restore it</div>
        </div>
      )}
    </div>
  );
}

// ── Dashboard Edit Panel (sidebar) ─────────────────────────────────────────
function DashboardEditPanel({ spec, rows, onUpdate, onClose, onEditStart, onEditEnd }) {
  const [prompt,setPrompt]   = useState("");
  const [loading,setLoading] = useState(false);
  const [error,setError]     = useState(null);
  const suggestions = [
    "Add a chart showing top 5 categories by value",
    "Add a KPI showing average value",
    "Add a table sorted by highest values",
    "Change the color theme to blue",
    "Add insights about trends",
    "Show a trend line chart",
  ];
  const handleEdit = async () => {
    if (!prompt.trim()||loading) return;
    setLoading(true); setError(null); onEditStart?.();
    const token=localStorage.getItem("konet_token");
    try {
      const res=await fetch(`${API_BASE}/rag/dashboard/edit`,{method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},body:JSON.stringify({spec,rows:rows.slice(0,50),edit_prompt:prompt})});
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data=await res.json();
      if (data.spec){onUpdate(data.spec);onClose();}
    } catch(e){setError(e.message);}
    finally{setLoading(false);onEditEnd?.();}
  };
  return (
    <div style={{position:"fixed",right:0,top:0,bottom:0,width:380,background:T.surface,borderLeft:`1px solid ${T.border}`,zIndex:200,display:"flex",flexDirection:"column",boxShadow:"-4px 0 24px rgba(0,0,0,0.12)",animation:"dashSlideIn 0.22s ease"}}>
      {/* Header */}
      <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div>
          <div style={{color:T.text,fontSize:15,fontWeight:700}}>✏️ Edit Dashboard</div>
          <div style={{color:T.textDim,fontSize:10.5,marginTop:2}}>{loading?"Applying your changes…":"Describe what you want to change"}</div>
        </div>
        <button onClick={onClose} disabled={loading} style={{background:"none",border:"none",cursor:loading?"not-allowed":"pointer",color:T.textDim,fontSize:20,opacity:loading?0.4:1,lineHeight:1}}>×</button>
      </div>
      {/* Body */}
      {loading?(
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:20,padding:24}}>
          <div style={{display:"flex",gap:5,alignItems:"center",height:48}}>
            {[0,1,2,3,4].map(i=><div key={i} style={{width:6,height:40,borderRadius:4,background:`hsl(${4+i*8},75%,${42+i*4}%)`,animation:"dashWave 0.85s ease-in-out infinite",animationDelay:`${i*0.13}s`,transformOrigin:"center"}}/>)}
          </div>
          <div style={{textAlign:"center",animation:"dashFadeIn 0.4s ease"}}>
            <div style={{color:T.text,fontSize:13.5,fontWeight:700,marginBottom:6}}>✨ Applying changes…</div>
            <div style={{color:T.textDim,fontSize:11.5,lineHeight:1.6,maxWidth:260}}>KOINE AI INSIGHTS  is updating your dashboard based on your instructions ....</div>
          </div>
          {prompt&&<div style={{width:"100%",padding:"10px 14px",background:T.accentSoft,border:`1px solid ${T.border}`,borderRadius:8,fontSize:11.5,color:T.textMid,fontStyle:"italic",lineHeight:1.55,animation:"dashFadeIn 0.5s 0.1s ease backwards"}}>"{prompt.length>120?prompt.slice(0,120)+"…":prompt}"</div>}
        </div>
      ):(
        <div style={{flex:1,overflow:"auto",padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <textarea value={prompt} onChange={e=>setPrompt(e.target.value)} placeholder="e.g. Add a pie chart showing category distribution, add KPI for total count, append a table of top values…" style={{width:"100%",minHeight:110,padding:10,borderRadius:8,border:`1px solid ${T.border}`,background:T.bgAlt,color:T.text,fontSize:12,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}} onKeyDown={e=>{if(e.key==="Enter"&&e.metaKey)handleEdit();}}/>
          <div style={{color:T.textDim,fontSize:10,marginBottom:2}}>Quick suggestions:</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {suggestions.map(s=><button key={s} onClick={()=>setPrompt(s)} style={{background:T.accentSoft,border:`1px solid ${T.borderMed}`,color:T.textMid,padding:"4px 10px",borderRadius:16,fontSize:10,cursor:"pointer"}}>{s}</button>)}
          </div>
          {error&&<div style={{color:T.neg,fontSize:11,padding:"8px 12px",background:"rgba(196,32,32,0.08)",borderRadius:6}}>Error: {error}</div>}
        </div>
      )}
      {/* Footer */}
      <div style={{padding:"12px 16px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8,flexShrink:0}}>
        <button onClick={onClose} disabled={loading} style={{flex:1,padding:"8px 0",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:loading?T.textDim:T.textMid,cursor:loading?"not-allowed":"pointer",fontSize:12}}>Cancel</button>
        <button onClick={handleEdit} disabled={!prompt.trim()||loading} style={{flex:2,padding:"8px 0",borderRadius:8,border:"none",background:!prompt.trim()||loading?T.border:T.accent,color:"#fff",cursor:!prompt.trim()||loading?"default":"pointer",fontSize:12,fontWeight:700}}>
          {loading?"⏳ Working…":"✨ Apply Changes"}
        </button>
      </div>
    </div>
  );
}

// ── Dynamic Chart ──────────────────────────────────────────────────────────
const CHART_COLORS=["#E8001A","#1A7A9A","#1A7A3A","#B06000","#7A3A8A","#C42020","#0A5A7A","#5A8A2A","#9A3A6A","#3A6A9A"];

function aggregateForChart(rows, chart) {
  const {xKey,yKeys=[],aggregation="sum",limit=20,sortBy="value",sortDir="desc"}=chart;
  if (!rows.length||!xKey) return [];
  const yKey=yKeys[0]?.key; if (!yKey) return [];
  const groups={};
  rows.forEach(row=>{
    const xVal=row[xKey]??"(blank)"; if (!groups[xVal]) groups[xVal]={};
    yKeys.forEach(({key})=>{ if (!groups[xVal][key]) groups[xVal][key]=[]; const v=Number(row[key]); if (!isNaN(v)) groups[xVal][key].push(v); });
  });
  let result=Object.entries(groups).map(([x,yMap])=>{
    const entry={[xKey]:x};
    yKeys.forEach(({key})=>{ const vals=yMap[key]||[]; if (!vals.length){entry[key]=0;return;} switch(aggregation){case"avg":entry[key]=vals.reduce((a,b)=>a+b,0)/vals.length;break;case"count":entry[key]=vals.length;break;case"max":entry[key]=Math.max(...vals);break;case"min":entry[key]=Math.min(...vals);break;default:entry[key]=vals.reduce((a,b)=>a+b,0);} entry[key]=Math.round(entry[key]*100)/100; });
    return entry;
  });
  if (sortBy==="value") result.sort((a,b)=>sortDir==="asc"?a[yKey]-b[yKey]:b[yKey]-a[yKey]);
  else result.sort((a,b)=>sortDir==="asc"?String(a[xKey]).localeCompare(String(b[xKey])):String(b[xKey]).localeCompare(String(a[xKey])));
  return result.slice(0,limit);
}

function DynamicChart({ chart, rows }) {
  const data=useMemo(()=>aggregateForChart(rows,chart),[rows,chart]);
  const {type,xKey,yKeys=[],showGrid=true}=chart;
  const colors=yKeys.map((y,i)=>y.color||CHART_COLORS[i%CHART_COLORS.length]);
  if (!data.length) return <div style={{color:T.textDim,textAlign:"center",padding:40,fontSize:12}}>No data to display</div>;
  const CM={data,margin:{top:10,right:20,bottom:40,left:10}};
  const axes=(horiz=false)=>(<>{showGrid&&<CartesianGrid strokeDasharray="2 4" stroke={T.border}/>}{horiz?<><XAxis type="number" tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/><YAxis type="category" dataKey={xKey} tick={{fill:T.textMid,fontSize:10}} axisLine={false} tickLine={false} width={110}/></>:<><XAxis dataKey={xKey} tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false} angle={-30} textAnchor="end" height={55}/><YAxis tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/></>}<Tooltip content={<GlassTip/>}/></>);

  if (type==="pie"||type==="donut") {
    const yk=yKeys[0]?.key, total=data.reduce((s,d)=>s+(d[yk]||0),0);
    return (<div style={{display:"flex",alignItems:"center",gap:16}}><div style={{position:"relative",width:180,height:180,flexShrink:0}}><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={data} dataKey={yk} nameKey={xKey} innerRadius={type==="donut"?"55%":"0%"} outerRadius="85%" stroke="#fff" strokeWidth={2} paddingAngle={2}>{data.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}</Pie><Tooltip content={<GlassTip/>}/></PieChart></ResponsiveContainer>{type==="donut"&&<div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center"}}><div style={{color:T.textDim,fontSize:9}}>Total</div><div style={{color:T.text,fontSize:13,fontWeight:700}}>{total>1e6?(total/1e6).toFixed(1)+"M":total>1000?(total/1000).toFixed(1)+"K":total.toFixed(1)}</div></div>}</div><div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>{data.slice(0,8).map((d,i)=><div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:10.5}}><span style={{display:"flex",alignItems:"center",gap:5}}><span style={{width:8,height:8,borderRadius:2,background:CHART_COLORS[i%CHART_COLORS.length],flexShrink:0}}/><span style={{color:T.textMid}}>{String(d[xKey]).slice(0,22)}</span></span><span style={{color:T.text,fontWeight:600}}>{fmtValue(d[yk],"number")}</span></div>)}</div></div>);
  }
  if (type==="hbar") return (<ResponsiveContainer width="100%" height={Math.max(220,data.length*30+40)}><ComposedChart {...CM} layout="vertical" margin={{top:5,right:70,bottom:5,left:110}}>{axes(true)}{yKeys.map((y,i)=><Bar key={y.key} dataKey={y.key} name={y.label||y.key} fill={colors[i]} radius={[3,3,3,3]} fillOpacity={0.85}><LabelList dataKey={y.key} position="right" style={{fill:T.textMid,fontSize:10}} formatter={v=>v>1000?(v/1000).toFixed(1)+"K":v?.toFixed?.(1)??v}/></Bar>)}</ComposedChart></ResponsiveContainer>);
  if (type==="scatter") { const xS=xKey,yS=yKeys[0]?.key, sData=rows.slice(0,200).map(r=>({x:Number(r[xS])||0,y:Number(r[yS])||0})).filter(d=>!isNaN(d.x)&&!isNaN(d.y)); return (<ResponsiveContainer width="100%" height={280}><ScatterChart margin={{top:10,right:20,bottom:20,left:0}}><CartesianGrid strokeDasharray="2 4" stroke={T.border}/><XAxis type="number" dataKey="x" name={xS} tick={{fill:T.textDim,fontSize:10}} label={{value:xS,position:"insideBottom",offset:-6,fill:T.textDim,fontSize:10}}/><YAxis type="number" dataKey="y" name={yS} tick={{fill:T.textDim,fontSize:10}} label={{value:yS,angle:-90,position:"insideLeft",fill:T.textDim,fontSize:10}}/><Tooltip cursor={{stroke:T.accent,strokeDasharray:"3 3"}} content={<GlassTip/>}/><Scatter data={sData} fill={T.accent} fillOpacity={0.6}/></ScatterChart></ResponsiveContainer>); }
  if (type==="radar") return (<ResponsiveContainer width="100%" height={280}><RadarChart data={data} margin={{top:10,right:30,bottom:10,left:30}}><PolarGrid stroke={T.border}/><PolarAngleAxis dataKey={xKey} tick={{fill:T.textMid,fontSize:10}}/><PolarRadiusAxis tick={{fill:T.textDim,fontSize:9}}/>{yKeys.map((y,i)=><Radar key={y.key} name={y.label||y.key} dataKey={y.key} stroke={colors[i]} fill={colors[i]} fillOpacity={0.25} strokeWidth={2}/>)}<Tooltip content={<GlassTip/>}/></RadarChart></ResponsiveContainer>);
  if (type==="area") return (<ResponsiveContainer width="100%" height={260}><AreaChart {...CM}><defs>{yKeys.map((y,i)=><linearGradient key={y.key} id={`llmGrad-${y.key}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={colors[i]} stopOpacity={0.35}/><stop offset="95%" stopColor={colors[i]} stopOpacity={0}/></linearGradient>)}</defs>{axes()}{yKeys.map((y,i)=><Area key={y.key} type="monotone" dataKey={y.key} name={y.label||y.key} stroke={colors[i]} strokeWidth={2} fill={`url(#llmGrad-${y.key})`} dot={false}/>)}</AreaChart></ResponsiveContainer>);
  if (type==="line") return (<ResponsiveContainer width="100%" height={260}><LineChart {...CM}>{axes()}{yKeys.map((y,i)=><Line key={y.key} type="monotone" dataKey={y.key} name={y.label||y.key} stroke={colors[i]} strokeWidth={2} dot={{r:3,fill:colors[i]}} activeDot={{r:5}}/>)}</LineChart></ResponsiveContainer>);
  if (type==="treemap") { const yk=yKeys[0]?.key,tot=data.reduce((s,d)=>s+(d[yk]||0),0)||1; return (<div style={{display:"flex",flexWrap:"wrap",gap:2,height:240,alignContent:"flex-start",overflow:"hidden"}}>{data.map((d,i)=>{const pct=(d[yk]||0)/tot;return(<div key={i} title={`${d[xKey]}: ${d[yk]}`} style={{background:CHART_COLORS[i%CHART_COLORS.length],width:`${Math.max(8,Math.sqrt(pct)*100)}%`,flexGrow:pct*50,height:60+pct*160,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"#fff",fontWeight:700,textAlign:"center",padding:2,overflow:"hidden",opacity:0.88}}>{String(d[xKey]).slice(0,12)}</div>);})}</div>); }
  if (type==="funnel") { const yk=yKeys[0]?.key,maxV=Math.max(...data.map(d=>d[yk]||0))||1; return (<div style={{display:"flex",flexDirection:"column",gap:3,padding:"8px 0"}}>{data.map((d,i)=>{const pct=(d[yk]||0)/maxV;return(<div key={i} style={{display:"flex",alignItems:"center",gap:8}}><div style={{width:90,textAlign:"right",fontSize:10,color:T.textMid,flexShrink:0}}>{String(d[xKey]).slice(0,14)}</div><div style={{flex:1,display:"flex",justifyContent:"center"}}><div style={{width:`${pct*100}%`,height:28,background:CHART_COLORS[i%CHART_COLORS.length],borderRadius:4,opacity:0.85,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",fontWeight:700,minWidth:40}}>{d[yk]?.toLocaleString?.()??d[yk]}</div></div></div>);})}</div>); }
  if (type==="waterfall") {
    const yk=yKeys[0]?.key; let running=0;
    const wfData=data.map(d=>{const val=d[yk]||0,base=running;running+=val;return{...d,base,val,isNeg:val<0};});
    const allV=wfData.map(d=>[d.base,d.base+d.val]).flat();
    return (<ResponsiveContainer width="100%" height={260}><ComposedChart data={wfData} margin={{top:10,right:20,bottom:40,left:10}}><CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false}/><XAxis dataKey={xKey} tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false} angle={-30} textAnchor="end" height={55}/><YAxis domain={[Math.min(0,...allV),Math.max(...allV)]} tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/><Tooltip content={<GlassTip/>}/><Bar dataKey="val" stackId="a">{wfData.map((d,i)=><Cell key={i} fill={d.isNeg?T.neg:T.pos} fillOpacity={0.85}/>)}</Bar></ComposedChart></ResponsiveContainer>);
  }
  return (<ResponsiveContainer width="100%" height={260}><BarChart {...CM}>{axes()}{yKeys.map((y,i)=><Bar key={y.key} dataKey={y.key} name={y.label||y.key} fill={colors[i]} radius={[3,3,0,0]} fillOpacity={0.85}>{yKeys.length===1&&data.map((_,idx)=><Cell key={idx} fill={CHART_COLORS[idx%CHART_COLORS.length]}/>)}</Bar>)}</BarChart></ResponsiveContainer>);
}

// ── Dynamic Table ──────────────────────────────────────────────────────────
function DynamicTable({ table, rows }) {
  const {columns=[],sortBy,sortDir="desc",limit=15,highlight="none"}=table;
  const sorted=useMemo(()=>{
    let r=[...rows];
    if (sortBy) r.sort((a,b)=>{const av=Number(a[sortBy]),bv=Number(b[sortBy]);if(!isNaN(av)&&!isNaN(bv))return sortDir==="asc"?av-bv:bv-av;return sortDir==="asc"?String(a[sortBy]).localeCompare(String(b[sortBy])):String(b[sortBy]).localeCompare(String(a[sortBy]));});
    return r.slice(0,limit);
  },[rows,sortBy,sortDir,limit]);
  return (
    <div style={{overflowX:"auto",maxHeight:360,overflowY:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
        <thead style={{position:"sticky",top:0,background:T.surface,zIndex:1}}>
          <tr>
            <th style={{padding:"6px 8px",color:T.textDim,fontSize:9,textTransform:"uppercase",letterSpacing:1,borderBottom:`1px solid ${T.border}`,textAlign:"center",width:32}}>#</th>
            {columns.map(col=><th key={col.key} style={{padding:"6px 8px",color:T.textDim,fontSize:9,textTransform:"uppercase",letterSpacing:1,borderBottom:`1px solid ${T.border}`,textAlign:col.format==="text"?"left":"right",whiteSpace:"nowrap"}}>{col.label||col.key}</th>)}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row,ri)=>{
            const isTop=highlight==="top"&&ri<3, isBot=highlight==="bottom"&&ri>=sorted.length-3;
            return (<tr key={ri} style={{background:isTop?"rgba(26,122,58,0.06)":isBot?"rgba(196,32,32,0.06)":"transparent"}}>
              <td style={{padding:"5px 8px",color:T.textDim,fontSize:10,textAlign:"center",borderBottom:`1px solid ${T.border}`}}>{isTop?"🥇":isBot?"⚠":ri+1}</td>
              {columns.map(col=>{const raw=row[col.key],num=Number(raw);return(<td key={col.key} style={{padding:"5px 8px",borderBottom:`1px solid ${T.border}`,textAlign:col.format==="text"?"left":"right",color:col.format==="percent"?growthColor(num/100):T.text,fontWeight:!isNaN(num)?600:400,fontSize:11}}>{fmtValue(raw,col.format)}</td>);})}
            </tr>);
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Dynamic Insight Card ───────────────────────────────────────────────────
function DynamicInsightCard({ insight }) {
  const colorMap={CRITICAL:T.neg,HIGH:T.warn,MEDIUM:T.accent,INFO:T.pos};
  const color=insight.color||colorMap[insight.priority]||T.accent;
  return (
    <div style={{background:`linear-gradient(140deg,${color}0d 0%,${T.surface} 100%)`,border:`1px solid ${color}33`,borderLeft:`3px solid ${color}`,borderRadius:10,padding:16,minHeight:180,display:"flex",flexDirection:"column",minWidth:260,flex:"1 1 260px",boxShadow:T.shadow}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}><span style={{fontSize:22}}>{insight.icon}</span><span style={{fontSize:9,fontWeight:700,letterSpacing:1.2,color,background:`${color}18`,padding:"2px 8px",borderRadius:4}}>{insight.priority}</span></div>
      <div style={{color:T.text,fontSize:13,fontWeight:700,marginBottom:8}}>{insight.title}</div>
      <div style={{color:T.textMid,fontSize:11,lineHeight:1.55,flex:1}}>{insight.narrative}</div>
      {insight.actions?.length>0&&<div style={{borderTop:`1px solid ${T.border}`,paddingTop:8,marginTop:10}}>{insight.actions.map((a,i)=><div key={i} style={{display:"flex",gap:6,fontSize:10,color:T.textMid,marginBottom:3}}><span style={{color}}>▸</span><span>{a}</span></div>)}</div>}
    </div>
  );
}
// ── Dashboard Loader ───────────────────────────────────────────────────────
const DASHBOARD_QUOTES=[
  {q:"Data is the new oil.",a:"— Clive Humby"},
  {q:"Without data, you're just another person with an opinion.",a:"— W. Edwards Deming"},
  {q:"In God we trust; all others must bring data.",a:"— W. Edwards Deming"},
  {q:"The goal is to turn data into information, and information into insight.",a:"— Carly Fiorina"},
  {q:"Numbers have an important story to tell.",a:"— Stephen Few"},
  {q:"Torture the data, and it will confess to anything.",a:"— Ronald Coase"},
];
function DashboardLoader({ message }) {
  const quote=useMemo(()=>DASHBOARD_QUOTES[Math.floor(Math.random()*DASHBOARD_QUOTES.length)],[]);
  return (
    <div style={{minHeight:"70vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:0,background:T.bgAlt}}>
      <div style={{display:"flex",alignItems:"flex-end",height:60,marginBottom:24}}>
        {[40,65,30,80,50,70,45,90,35,60].map((h,i)=><div key={i} style={{width:6,height:`${h}%`,margin:"0 2px",borderRadius:3,background:`hsl(${350+i*4},${70+i*2}%,${40+i*2}%)`,animation:"dashBarGrow 1.2s ease-out infinite alternate",animationDelay:`${i*0.12}s`}}/>)}
      </div>
      <div style={{position:"relative",width:80,height:80,marginBottom:28}}>
        <div style={{position:"absolute",inset:0,border:`2px solid ${T.border}`,borderRadius:"50%"}}/>
        <div style={{position:"absolute",inset:0,border:"2px solid transparent",borderTopColor:T.accent,borderRadius:"50%",animation:"dashSpin 1s linear infinite"}}/>
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>📊</div>
      </div>
      <div style={{color:T.text,fontSize:15,fontWeight:700,marginBottom:6,animation:"dashFadeUp 0.5s ease"}}>{message||"Building your dashboard…"}</div>
      <div style={{maxWidth:380,textAlign:"center",marginTop:20,padding:"16px 24px",background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,animation:"dashFadeUp 0.8s ease"}}>
        <div style={{color:T.textMid,fontSize:13,fontStyle:"italic",lineHeight:1.6}}>"{quote.q}"</div>
        <div style={{color:T.textDim,fontSize:10.5,marginTop:8,fontWeight:600}}>{quote.a}</div>
      </div>
    </div>
  );
}

const CONTEXT_EXAMPLES = [
  "This is a sales report. YTD Growth below 0% means the territory is declining. Key metric is NSR (Net Sales Revenue). Flag any market with MAT growth under 5%.",
  "Retail P&L data. Gross Margin % is the most important KPI. Compare actuals vs budget. Highlight any line where variance exceeds 10%.",
  "HR headcount file. Track attrition rate by department. Anything above 15% attrition is critical. Show trends over the last 6 months.",
  "Supply chain data. On-time delivery % is the primary KPI. Group by supplier and show worst performers first.",
  "Marketing spend data. Focus on Cost Per Acquisition (CPA) and ROAS. Lower CPA is better. Highlight channels with ROAS below 2x.",
];

function BusinessContextModal({ fileName, onSubmit, onSkip }) {
  const [text, setText]           = useState("");
  const [exampleIdx, setExampleIdx] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setExampleIdx(i => (i + 1) % CONTEXT_EXAMPLES.length), 3200);
    return () => clearInterval(iv);
  }, []);

  return (
    <div style={{
      minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px 16px", background: T.bgAlt,
    }}>
      <style>{`
        @keyframes dashFadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes ctxFade    { 0%{opacity:0;transform:translateY(4px)} 15%,85%{opacity:1;transform:translateY(0)} 100%{opacity:0;transform:translateY(-4px)} }
      `}</style>
      <div style={{
        width: "100%", maxWidth: 640,
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 16, overflow: "hidden",
        boxShadow: "0 8px 32px rgba(232,0,26,0.10)",
        animation: "dashFadeUp 0.4s ease",
      }}>
        {/* Header */}
        <div style={{ background: T.accent, padding: "20px 28px" }}>
          <div style={{ color: "#fff", fontSize: 18, fontWeight: 800, letterSpacing: -0.4 }}>
            🧠 Tell KOINE AI INSIGHTS  about your data
          </div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11.5, marginTop: 4 }}>
            Optional — but helps KOINE AI INSIGHTS  build a smarter dashboard for <strong style={{ color: "#fff" }}>{fileName || "your file"}</strong>
          </div>
        </div>

        <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Animated example */}
          <div style={{
            background: T.accentSoft, border: `1px solid ${T.borderMed}`,
            borderRadius: 10, padding: "12px 16px",
            minHeight: 60, display: "flex", alignItems: "center",
          }}>
            <div key={exampleIdx} style={{
              color: T.textMid, fontSize: 11.5, lineHeight: 1.6, fontStyle: "italic",
              animation: "ctxFade 3.2s ease forwards",
            }}>
              <span style={{ color: T.accent, fontWeight: 700, fontStyle: "normal" }}>e.g. </span>
              {CONTEXT_EXAMPLES[exampleIdx]}
            </div>
          </div>

          {/* Prompt hints */}
          <h4 style={{ color: T.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: 1.5 }}>
            Pro tips: include any relevant context, like…
          </h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {[
              "What does this data track?",
              "What are the key KPIs?",
              "Any thresholds or targets?",
              "Which metric matters most?",
              "Any business-specific terms?",
            ].map(hint => (
              <div key={hint} style={{
                fontSize: 10, color: T.textDim,
                background: T.bgAlt, border: `1px solid ${T.border}`,
                padding: "3px 10px", borderRadius: 999,
              }}>
                {hint}
              </div>
            ))}
          </div>

          {/* Textarea */}
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="e.g. This is a monthly P&L. Gross Margin % is the primary KPI. Flag any row where actuals miss budget by more than 10%. Key segments are Modern Trade, General Trade, and E-Commerce."
            style={{
              width: "100%", minHeight: 130, padding: "12px 14px",
              border: `1px solid ${text.trim() ? T.borderMed : T.border}`,
              borderRadius: 10, background: T.bgAlt, color: T.text,
              fontSize: 12.5, lineHeight: 1.6, resize: "vertical",
              fontFamily: "inherit", boxSizing: "border-box",
              outline: "none", transition: "border-color 0.15s",
            }}
            onFocus={e => { e.target.style.borderColor = T.accent; }}
            onBlur={e => { e.target.style.borderColor = text.trim() ? T.borderMed : T.border; }}
            onKeyDown={e => { if (e.key === "Enter" && e.metaKey) onSubmit(text); }}
          />

          {/* Character count */}
          {text.length > 0 && (
            <div style={{ color: T.textDim, fontSize: 10, marginTop: -12, textAlign: "right" }}>
              {text.length} chars
            </div>
          )}
 <button
              onClick={() => setText("Create an interactive market research dashboard for sparkling beverage brands (Coca-Cola, Sprite, Thums Up, Fanta, Limca, 7Up, Mountain Dew, Pepsi, Campa and more) using the BEACH framework. Support filters for Geography (All India / States), Age group, SEC, and Brand.\n\n**1. Salience Panel** — Dual KPI cards showing Top of Mind and Spontaneous Awareness with current value + period-over-period change indicator (green/red arrows).\n\n**2. Mind Measures Panel** — Horizontal bar charts for Consideration and Affinity Top Box scores, with change deltas annotated inline. Include an Imagery spider/radar chart per brand.\n\n**3. Brand Power Breakdown** — Stacked bar or grouped bar chart splitting Brand Power into Meaningful, Differentiation, and Salience components, with change callouts.\n\n**4. Usage Metrics** — Two side-by-side gauge or bullet charts for Past 7-Day usage and Weekly+ frequency, with trend sparklines.\n\n**5. Occasions Panel** — A bubble or treemap showing occasion sizes; a 100% stacked bar for share across Meals, OTG, Break, Snacking, Socialising; and a split donut for At-Home vs Away-From-Home.\n\n**6. Profiles Panel** — Heatmap or indexed bar charts for Age and SEC penetration by brand.\n\n**7. Funnel / Conversion Grid** — A sankey or funnel table showing Awareness → Consideration → Affinity → Trial → Usage conversion rates with period change.\n\nUse brand colors per logo. Highlight headroom scores prominently. Flag negative changes in red, positive in green. Keep layout card-based with consistent typography.")}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                border: `1px solid ${T.border}`, background: "transparent",
                color: T.textMid, cursor: "pointer", fontSize: 12, fontWeight: 600,
              }}
            >
              Load BEACH data pre built Prompt
            </button>
          {/* Actions */}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => onSkip()}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                border: `1px solid ${T.border}`, background: "transparent",
                color: T.textMid, cursor: "pointer", fontSize: 12, fontWeight: 600,
              }}
            >
              Skip — build without context
            </button>
            <button
              onClick={() => onSubmit(text)}
              style={{
                flex: 2, padding: "10px 0", borderRadius: 10, border: "none",
                background: T.accent, color: "#fff",
                cursor: "pointer", fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              ✨ Build Dashboard
              {text.trim() && <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 4, padding: "1px 7px", fontSize: 10 }}>with context</span>}
            </button>
          </div>

          <div style={{ color: T.textDim, fontSize: 10, textAlign: "center", lineHeight: 1.5 }}>
            Your instructions are only used to generate this dashboard and are not stored separately.<br/>
            ⌘ + Enter to submit
          </div>
        </div>
      </div>
    </div>
  );
}
// ── LLM Dashboard (non-HCCB) ──────────────────────────────────────────────
function LLMDashboard({ data, sessionId, onAskAI, onReset, showEdit, setShowEdit, specHistory, setSpecHistory, spec, setSpec, rows, setRows }) {
  const [sheets,setSheets]     = useState([]);
  const [loading,setLoading]   = useState(true);
  const [loadingMsg,setLoadMsg]= useState("Reading your file…");
  const [error,setError]       = useState(null);
  const [activeFilters,setFilters]=useState({});

const [showContextModal, setShowContextModal] = useState(true);
  const [businessContext, setBusinessContext]   = useState("");
  const [contextDraft, setContextDraft]         = useState("");

  const loadDashboard = useCallback((refresh = false, ctx = businessContext) => {
    if (!sessionId) return;
    setSpec(null); setRows([]); setLoading(true); setError(null);
    const msgs = ["Scanning all columns…","Detecting patterns…","Building charts…","Calculating KPIs…","Finalising dashboard…"];
    let mi = 0; setLoadMsg(msgs[0]);
    const iv = setInterval(() => { mi++; if (mi < msgs.length) setLoadMsg(msgs[mi]); }, 25000);
    const contextParam = ctx?.trim() ? `&business_context=${encodeURIComponent(ctx.trim())}` : "";
    fetchDashboardSpec(sessionId, refresh, contextParam)
      .then(async ({ spec: s, rows: r, sheets: sh }) => {
        setSpec(s); setRows(r||[]); setSheets(sh||[]);
        await saveDashboardVersion(sessionId, s, "Version 1 (original)");
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); })
      .finally(() => clearInterval(iv));
  }, [sessionId, businessContext]);

  useEffect(()=>{if(sessionId) loadDashboard(false);},[sessionId]);
  useEffect(() => {
    // Don't auto-load — wait for user to either submit context or skip
  }, []);
  const getRows=useCallback((sheetFilter)=>{
    let r=sheetFilter?rows.filter(row=>row._sheet===sheetFilter):rows;
    Object.entries(activeFilters).forEach(([key,val])=>{if(!val||val==="__all__")return;r=r.filter(row=>String(row[key]??"")===val);});
    return r;
  },[rows,activeFilters]);

  const handleUpdate=useCallback(async(newSpec)=>{
    setSpecHistory(prev=>[...prev,spec]);
    setSpec(newSpec);
    const vNum=specHistory.length+2;
    await saveDashboardVersion(sessionId,newSpec,`Version ${vNum}`);
  },[spec,specHistory,sessionId]);
// Show context modal before loading
console.log(showContextModal, data);
  if (showContextModal) return (
    <BusinessContextModal
      fileName={data?.fileName || ""}
      onSubmit={(ctx) => {
        setBusinessContext(ctx);
        setContextDraft(ctx);
        setShowContextModal(false);
        loadDashboard(false, ctx);
      }}
      onSkip={() => {
        setShowContextModal(false);
        loadDashboard(false, "");
      }}
    />
  );
  if (loading) return <DashboardLoader message={loadingMsg}/>;
  if (error) return (
    <div style={{padding:32,textAlign:"center"}}>
      <div style={{color:T.neg,fontSize:16,fontWeight:700,marginBottom:8}}>⚠ Dashboard generation failed</div>
      <div style={{color:T.textDim,fontSize:12,marginBottom:16,maxWidth:400,margin:"0 auto 16px"}}>{error}</div>
      <button onClick={()=>loadDashboard(true)} style={{background:T.accent,color:"#fff",border:"none",padding:"8px 20px",borderRadius:8,cursor:"pointer",fontWeight:700}}>Retry</button>
    </div>
  );
  if (!spec) return null;

  const kpis=spec.kpis||[], charts=spec.charts||[], tables=spec.tables||[], insights=spec.insights||[], filters=spec.filters||[];
  const sizeStyle=size=>({full:{gridColumn:"1 / -1"},half:{gridColumn:"span 2"},third:{gridColumn:"span 1"}}[size]||{gridColumn:"span 2"});

  return (
    <div style={{padding:16,display:"flex",flexDirection:"column",gap:16,paddingRight:showEdit?400:16}}>
      {/* Toolbar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{color:T.text,fontSize:14,fontWeight:700}}>{spec.title||"Dashboard"}<span style={{color:T.textDim,fontWeight:400,fontSize:11,marginLeft:8}}>· {rows.length} rows</span></div>
          {spec.subtitle&&<div style={{color:T.textDim,fontSize:11,marginTop:2}}>{spec.subtitle}</div>}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <VersionHistoryDropdown specHistory={specHistory} currentSpec={spec} sessionId={sessionId}
            onRestore={(s,fromSaved)=>{if(fromSaved){setSpec(s);}else{const t=specHistory[s];setSpecHistory(h=>h.slice(0,s));setSpec(t);}}}
          />
          {onAskAI&&<button onClick={onAskAI} style={{background:T.surface,border:`1px solid ${T.border}`,color:T.textMid,padding:"6px 14px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer"}}>💬 Ask AI</button>}
          <button onClick={()=>setShowEdit(p=>!p)} style={{background:showEdit?T.accent:T.surface,border:`1px solid ${showEdit?T.accent:T.border}`,color:showEdit?"#fff":T.textMid,padding:"6px 14px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer"}}>
            ✏️ {showEdit?"Editing…":"Edit Dashboard"}
          </button>

          {businessContext.trim() && (
            <div
              title={businessContext}
              style={{
                background: T.accentSoft, border: `1px solid ${T.borderMed}`,
                color: T.textMid, padding: "5px 10px", borderRadius: 8,
                fontSize: 10.5, cursor: "pointer", maxWidth: 180,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                display: "flex", alignItems: "center", gap: 5,
              }}
              onClick={() => setShowContextModal(true)}
            >
              🧠 <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {businessContext.length > 40 ? businessContext.slice(0, 38) + "…" : businessContext}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      {filters.length>0&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 14px",display:"flex",flexWrap:"wrap",gap:10,alignItems:"center"}}>
          <span style={{color:T.textDim,fontSize:10,textTransform:"uppercase",letterSpacing:1,fontWeight:700}}>Filters</span>
          {filters.map(f=>{
            const uniqueVals=[...new Set(rows.map(r=>r[f.column]).filter(v=>v!=null&&v!==""))].sort().slice(0,20);
            return (<div key={f.column} style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{color:T.textMid,fontSize:10.5,fontWeight:600}}>{f.label||f.column}:</span>
              <select value={activeFilters[f.column]||"__all__"} onChange={e=>setFilters(p=>({...p,[f.column]:e.target.value}))} style={{background:T.bgAlt,color:T.text,border:`1px solid ${T.border}`,borderRadius:6,padding:"3px 8px",fontSize:10.5,cursor:"pointer"}}>
                <option value="__all__">All</option>
                {uniqueVals.map(v=><option key={String(v)} value={String(v)}>{String(v)}</option>)}
              </select>
            </div>);
          })}
          {Object.values(activeFilters).some(v=>v&&v!=="__all__")&&<button onClick={()=>setFilters({})} style={{color:T.accent,fontSize:10,background:"none",border:"none",cursor:"pointer",fontWeight:700}}>↻ Reset</button>}
        </div>
      )}

      {/* KPIs */}
      {kpis.length>0&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10}}>
          {kpis.map(kpi=>{
            let val="—"; try{val=fmtValue(evalExpr(kpi.valueExpr,getRows(null)),kpi.format);}catch{}
            return (<div key={kpi.id} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:"14px 16px",boxShadow:T.shadow,borderTop:`3px solid ${kpi.color||T.accent}`}}>
              <div style={{color:T.textDim,fontSize:10,textTransform:"uppercase",letterSpacing:1,fontWeight:700}}>{kpi.icon} {kpi.label}</div>
              <div style={{color:T.text,fontSize:22,fontWeight:800,marginTop:6,letterSpacing:-0.5}}>{val}</div>
              {kpi.description&&<div style={{color:T.textDim,fontSize:9.5,marginTop:4}}>{kpi.description}</div>}
            </div>);
          })}
        </div>
      )}

      {/* Charts */}
      {charts.length>0&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
          {charts.map(chart=>(
            <div key={chart.id} style={{...sizeStyle(chart.size),background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:14,boxShadow:T.shadow}}>
              <div style={{marginBottom:8}}>
                <div style={{color:T.text,fontSize:13,fontWeight:700}}>{chart.title}</div>
                {chart.subtitle&&<div style={{color:T.textDim,fontSize:10,marginTop:2}}>{chart.subtitle}</div>}
              </div>
              <DynamicChart chart={chart} rows={getRows(chart.sheetFilter)}/>
              {chart.insight&&<div style={{marginTop:8,padding:"5px 8px",background:T.accentSoft,borderRadius:5,color:T.textMid,fontSize:10,borderLeft:`2px solid ${T.accent}`}}>💡 {chart.insight}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Tables */}
      {tables.map(table=>(
        <div key={table.id} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:14,boxShadow:T.shadow}}>
          <div style={{color:T.text,fontSize:13,fontWeight:700,marginBottom:10}}>{table.title}</div>
          <DynamicTable table={table} rows={getRows(table.sheetFilter)}/>
        </div>
      ))}

      {/* Insights */}
      {insights.length>0&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:16,boxShadow:T.shadow}}>
          <div style={{color:T.text,fontSize:15,fontWeight:800,marginBottom:12}}><span style={{color:T.accent,marginRight:8}}>✦</span>AI Insights</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
            {insights.map((ins,i)=><DynamicInsightCard key={i} insight={ins}/>)}
          </div>
        </div>
      )}

      {/* Edit panel */}
      {showEdit&&<DashboardEditPanel spec={spec} rows={rows} onUpdate={handleUpdate} onClose={()=>setShowEdit(false)}/>}
    </div>
  );
}

// ── HCCB Charts ────────────────────────────────────────────────────────────
function Heatmap({ cbos, onSelect, selected }) {
  const metrics=[{key:"ytdGr",label:"YTD Gr"},{key:"qtdGr",label:"QTD Gr"},{key:"mtdGr",label:"MTD Gr"},{key:"cagr2y",label:"2Y CAGR"},{key:"volMix",label:"Vol Mix"}];
  return (
    <div style={{overflowX:"auto"}}>
      <div style={{display:"grid",gridTemplateColumns:`170px repeat(${metrics.length},1fr)`,gap:3,minWidth:460}}>
        <div/>
        {metrics.map(m=><div key={m.key} style={{color:T.textDim,fontSize:9.5,textTransform:"uppercase",letterSpacing:1,textAlign:"center",paddingBottom:6,fontWeight:700}}>{m.label}</div>)}
        {cbos.map(c=>(
          <Fragment key={c.l3}>
            <div onClick={()=>onSelect&&onSelect(c.l3)} style={{padding:"6px 8px",fontSize:11,color:selected===c.l3?T.accent:T.text,cursor:"pointer",borderLeft:`2px solid ${REGION_COLOR[c.l2]||T.accent}`,background:selected===c.l3?T.accentSoft:"transparent",display:"flex",alignItems:"center",gap:6}}>
              <span style={{flex:1,fontWeight:selected===c.l3?700:400}}>{c.l3}</span>
              <span style={{fontSize:9,color:T.textDim}}>{fmtVolMnShort(c.vol)}</span>
            </div>
            {metrics.map(m=>{
              const v=c[m.key];
              return (<div key={m.key} onClick={()=>onSelect&&onSelect(c.l3)} style={{background:m.key==="volMix"?`rgba(232,0,26,${0.08+v*1.5})`:heatColor(v),padding:"6px 4px",textAlign:"center",fontSize:11.5,color:T.text,cursor:"pointer",borderRadius:3,fontWeight:500}}>{m.key==="volMix"?fmtPct(v):fmtPctSign(v)}</div>);
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function GenericDataTable({ cbos, onSelect, selected, colLabels }) {
  const [sortKey,setSortKey]=useState("vol"), [sortDir,setSortDir]=useState(-1);
  const sorted=[...cbos].sort((a,b)=>(b[sortKey]-a[sortKey])*sortDir);
  const metricKeys=["vol","ytdGr","qtdGr","mtdGr","cagr2y"];
  const metricLabels={vol:colLabels?.vol||"Volume",ytdGr:colLabels?.ytdGr||"YTD Growth",qtdGr:colLabels?.qtdGr||"QTD Growth",mtdGr:colLabels?.mtdGr||"MTD Growth",cagr2y:colLabels?.cagr2y||"2Y CAGR"};
  const isGrowth=k=>k!=="vol"&&k!=="volMix";
  const handleSort=k=>{if(sortKey===k)setSortDir(d=>-d);else{setSortKey(k);setSortDir(-1);}};
  return (
    <div style={{overflowX:"auto",maxHeight:380,overflowY:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
        <thead style={{position:"sticky",top:0,background:T.surface,zIndex:1}}>
          <tr>
            <th onClick={()=>handleSort("l3")} style={{textAlign:"left",padding:"6px 8px",color:T.textDim,fontSize:9.5,textTransform:"uppercase",letterSpacing:1,fontWeight:700,borderBottom:`1px solid ${T.border}`,cursor:"pointer"}}>{colLabels?.segCol||"Segment"} {sortKey==="l3"?(sortDir>0?"▲":"▼"):""}</th>
            {colLabels?.groupCol&&<th style={{textAlign:"left",padding:"6px 8px",color:T.textDim,fontSize:9.5,textTransform:"uppercase",letterSpacing:1,fontWeight:700,borderBottom:`1px solid ${T.border}`}}>{colLabels.groupCol}</th>}
            {metricKeys.map(k=><th key={k} onClick={()=>handleSort(k)} style={{textAlign:"right",padding:"6px 8px",color:sortKey===k?T.accent:T.textDim,fontSize:9.5,textTransform:"uppercase",letterSpacing:1,fontWeight:700,borderBottom:`1px solid ${T.border}`,cursor:"pointer",whiteSpace:"nowrap"}}>{metricLabels[k]} {sortKey===k?(sortDir>0?"▲":"▼"):""}</th>)}
            <th style={{textAlign:"right",padding:"6px 8px",color:T.textDim,fontSize:9.5,textTransform:"uppercase",letterSpacing:1,fontWeight:700,borderBottom:`1px solid ${T.border}`}}>Mix</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(c=>(
            <tr key={c.l3} onClick={()=>onSelect?.(c.l3)} style={{cursor:"pointer",background:selected===c.l3?T.accentSoft:"transparent",borderLeft:selected===c.l3?`2px solid ${T.accent}`:"2px solid transparent"}}>
              <td style={{padding:"6px 8px",color:selected===c.l3?T.accent:T.text,fontWeight:selected===c.l3?700:400,borderBottom:`1px solid ${T.border}`}}>{c.l3}</td>
              {colLabels?.groupCol&&<td style={{padding:"6px 8px",color:T.textDim,fontSize:10,borderBottom:`1px solid ${T.border}`}}>{c.l2}</td>}
              {metricKeys.map(k=><td key={k} style={{textAlign:"right",padding:"6px 8px",color:isGrowth(k)?growthColor(c[k]):T.text,fontWeight:600,borderBottom:`1px solid ${T.border}`}}>{isGrowth(k)?fmtPctSign(c[k]):(c[k]>1000?c[k].toLocaleString(undefined,{maximumFractionDigits:0}):c[k]?.toFixed?.(2)??"—")}</td>)}
              <td style={{textAlign:"right",padding:"6px 8px",color:T.textMid,borderBottom:`1px solid ${T.border}`}}>{fmtPct(c.volMix)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MoMTrend({ data, selectedKeys }) {
  const palette=[T.accent,T.pos,T.warn,"#1A7A9A","#7A3A8A","#5A3A9A"];
  const chartData=data.months.map((m,i)=>{
    const row={m:String(m).replace(" 20"," '")};
    selectedKeys.forEach(k=>{row[k]=data.momData[k]?.[i]??null;});
    if(data.momData["HCCB+ Total"]) row["Portfolio Avg"]=data.momData["HCCB+ Total"]?.[i]??null;
    return row;
  });
  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={chartData} margin={{top:10,right:12,bottom:0,left:-8}}>
        <CartesianGrid strokeDasharray="2 4" stroke={T.border}/>
        <XAxis dataKey="m" tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/>
        <YAxis tickFormatter={v=>`${(v*100).toFixed(0)}%`} tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/>
        <Tooltip content={<GlassTip/>}/>
        <ReferenceLine y={0} stroke={T.textLight} strokeDasharray="3 3"/>
        {data.momData["HCCB+ Total"]&&<Line dataKey="Portfolio Avg" type="monotone" stroke={T.textLight} strokeDasharray="4 4" strokeWidth={1.2} dot={false}/>}
        {selectedKeys.map((k,i)=><Line key={k} dataKey={k} name={k} type="monotone" stroke={palette[i%palette.length]} strokeWidth={2} dot={{r:2.5,fill:palette[i%palette.length],strokeWidth:0}} activeDot={{r:5}}/>)}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function GenericTrend({ data, selectedKeys }) {
  const palette=[T.accent,T.pos,T.warn,"#1A7A9A","#7A3A8A","#5A3A9A"];
  if (!data.months.length||!Object.keys(data.momData).length) return <div style={{color:T.textDim,textAlign:"center",padding:40}}>No time-series data found</div>;
  const chartData=data.months.map((m,i)=>{const row={m:String(m)};selectedKeys.forEach(k=>{row[k]=data.momData[k]?.[i]??null;});return row;});
  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={chartData} margin={{top:10,right:12,bottom:0,left:-8}}>
        <CartesianGrid strokeDasharray="2 4" stroke={T.border}/>
        <XAxis dataKey="m" tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/>
        <YAxis tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/>
        <Tooltip content={<GlassTip/>}/>
        <ReferenceLine y={0} stroke={T.textLight} strokeDasharray="3 3"/>
        {selectedKeys.map((k,i)=><Line key={k} dataKey={k} name={k} type="monotone" stroke={palette[i%palette.length]} strokeWidth={2} dot={{r:2.5,fill:palette[i%palette.length],strokeWidth:0}} activeDot={{r:5}}/>)}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function CategoryDonut({ rows }) {
  const totals=CATEGORIES.map(c=>{const slice=rows.filter(r=>r.cat===c),vol=slice.reduce((s,r)=>s+(r.vol||0),0);return{name:c,value:vol,color:CAT_COLOR[c],ytdGr:aggGrowth(slice,"ytdGr")};}).filter(t=>t.value>0);
  const totalVol=totals.reduce((s,r)=>s+r.value,0);
  if (!totals.length) return <div style={{color:T.textDim,textAlign:"center",padding:40,fontSize:13}}>No category data</div>;
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
      <div style={{position:"relative",width:"100%",maxWidth:200,aspectRatio:"1",margin:"0 auto"}}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={totals} dataKey="value" innerRadius="62%" outerRadius="92%" stroke="#fff" strokeWidth={2} paddingAngle={2}>
              {totals.map((d,i)=><Cell key={i} fill={d.color}/>)}
            </Pie>
            <Tooltip content={({active,payload})=>{if(!active||!payload?.length)return null;const p=payload[0].payload;return(<div style={{background:"rgba(255,255,255,0.97)",border:`1px solid ${T.borderMed}`,borderRadius:8,padding:10,fontSize:11,color:T.text,boxShadow:T.shadowMd}}><div style={{color:p.color,fontWeight:700}}>{p.name}</div><div>Mix: {fmtPct(p.value/totalVol)}</div><div>YTD: <span style={{color:growthColor(p.ytdGr)}}>{fmtPctSign(p.ytdGr)}</span></div></div>);}}/>
          </PieChart>
        </ResponsiveContainer>
        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center",pointerEvents:"none"}}>
          <div style={{color:T.textDim,fontSize:9,textTransform:"uppercase",letterSpacing:1}}>Total</div>
          <div style={{color:T.text,fontSize:16,fontWeight:700,marginTop:2}}>{fmtVolMn(totalVol)}</div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,width:"100%",marginTop:12}}>
        {totals.map(t=><div key={t.name} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:10.5}}><span style={{display:"flex",alignItems:"center",gap:5}}><span style={{width:8,height:8,borderRadius:2,background:t.color}}/><span style={{color:T.textMid}}>{t.name}</span></span><span style={{color:growthColor(t.ytdGr),fontWeight:600,fontSize:10}}>{fmtPctSign(t.ytdGr)}</span></div>)}
      </div>
    </div>
  );
}

function GenericPie({ cbos }) {
  const COLORS=[T.accent,T.accentDark,"#800010","#1A7A9A","#7A3A8A",T.pos,T.warn,"#E06010","#5A3A9A","#2A7A8A"];
  const totalVol=cbos.reduce((s,c)=>s+(c.vol||0),0)||1;
  const d=cbos.slice(0,10).map(c=>({name:c.l3,value:c.vol||0})).filter(d=>d.value>0);
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
      <div style={{position:"relative",width:"100%",maxWidth:200,aspectRatio:"1",margin:"0 auto"}}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={d} dataKey="value" innerRadius="62%" outerRadius="92%" stroke="#fff" strokeWidth={2} paddingAngle={2}>
              {d.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
            </Pie>
            <Tooltip content={({active,payload})=>{if(!active||!payload?.length)return null;const p=payload[0];return(<div style={{background:"rgba(255,255,255,0.97)",border:`1px solid ${T.borderMed}`,borderRadius:8,padding:10,fontSize:11,color:T.text,boxShadow:T.shadowMd}}><div style={{fontWeight:700}}>{p.name}</div><div>Share: {fmtPct(p.value/totalVol)}</div></div>);}}/>
          </PieChart>
        </ResponsiveContainer>
        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center",pointerEvents:"none"}}>
          <div style={{color:T.textDim,fontSize:9,textTransform:"uppercase",letterSpacing:1}}>Total</div>
          <div style={{color:T.text,fontSize:14,fontWeight:700,marginTop:2}}>{totalVol>1000?(totalVol/1000).toFixed(1)+"K":totalVol.toFixed(0)}</div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,width:"100%",marginTop:8}}>
        {d.slice(0,6).map((item,i)=><div key={item.name} style={{display:"flex",alignItems:"center",gap:5,fontSize:10}}><span style={{width:8,height:8,borderRadius:2,background:COLORS[i%COLORS.length],flexShrink:0}}/><span style={{color:T.textMid,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={item.name}>{item.name}</span></div>)}
      </div>
    </div>
  );
}

function CBOWaterfall({ cbos, metric, avgVal, onSelect, selected }) {
  const sorted=[...cbos].sort((a,b)=>b[metric]-a[metric]);
  const data=sorted.map(c=>({name:c.l3,l2:c.l2,v:c[metric],vol:c.vol}));
  const isGrowth=data.some(d=>Math.abs(d.v)<5);
  const COLORS_L=[T.accent,"#1A7A9A",T.pos,T.warn,"#7A3A8A","#800010"];
  const groups=[...new Set(cbos.map(c=>c.l2))];
  const cm={};
  groups.forEach((g,i)=>{cm[g]=REGION_COLOR[g]||COLORS_L[i%COLORS_L.length];});
  return (
    <ResponsiveContainer width="100%" height={Math.max(260,cbos.length*28+40)}>
      <ComposedChart data={data} layout="vertical" margin={{top:5,right:70,bottom:0,left:100}}>
        <CartesianGrid strokeDasharray="2 4" stroke={T.border} horizontal={false}/>
        <XAxis type="number" tickFormatter={v=>isGrowth?`${(v*100).toFixed(0)}%`:v.toLocaleString()} tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/>
        <YAxis type="category" dataKey="name" tick={{fill:T.textMid,fontSize:10.5}} axisLine={false} tickLine={false} width={100}/>
        <Tooltip content={({active,payload})=>{if(!active||!payload?.length)return null;const d=payload[0].payload;return(<div style={{background:"rgba(255,255,255,0.97)",border:`1px solid ${T.borderMed}`,borderRadius:8,padding:10,fontSize:11,color:T.text,boxShadow:T.shadowMd}}><div style={{color:cm[d.l2]||T.accent,fontWeight:700}}>{d.name}</div><div>Value: <span style={{color:growthColor(d.v),fontWeight:700}}>{isGrowth?fmtPctSign(d.v):d.v?.toLocaleString?.()}</span></div></div>);}} cursor={{fill:`${T.accent}08`}}/>
        <ReferenceLine x={0} stroke={T.textLight}/>
        {avgVal!==undefined&&<ReferenceLine x={avgVal} stroke={T.warn} strokeDasharray="4 3"/>}
        <Bar dataKey="v" radius={[3,3,3,3]} onClick={d=>onSelect&&onSelect(d.name)}>
          {data.map((d,i)=><Cell key={i} fill={isGrowth?(d.v>=0?T.pos:T.neg):T.accent} fillOpacity={selected&&selected!==d.name?0.3:0.85} stroke={selected===d.name?T.text:"none"} strokeWidth={selected===d.name?1.5:0}/>)}
          <LabelList dataKey="v" position="right" formatter={v=>isGrowth?fmtPctSign(v,1):v?.toLocaleString?.()??v} style={{fill:T.textMid,fontSize:10}}/>
        </Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function QuadrantBubble({ cbos, onSelect, selected }) {
  const COLORS_L=[T.accent,"#1A7A9A",T.pos,T.warn,"#7A3A8A","#800010"];
  const groups=[...new Set(cbos.map(c=>c.l2))];
  const cm={};
  groups.forEach((g,i)=>{cm[g]=REGION_COLOR[g]||COLORS_L[i%COLORS_L.length];});
  const data=cbos.map(c=>({name:c.l3,l2:c.l2,x:c.ytdGr,y:c.cagr2y,z:c.vol,fill:cm[c.l2]||T.accent}));
  return (
    <div style={{position:"relative",height:320}}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{top:14,right:22,bottom:22,left:8}}>
          <CartesianGrid strokeDasharray="2 4" stroke={T.border}/>
          <XAxis type="number" dataKey="x" tickFormatter={v=>`${(v*100).toFixed(0)}%`} tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false} label={{value:"Metric 1 →",position:"insideBottom",offset:-6,fill:T.textDim,fontSize:10}}/>
          <YAxis type="number" dataKey="y" tickFormatter={v=>`${(v*100).toFixed(0)}%`} tick={{fill:T.textDim,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false} label={{value:"Metric 2 ↑",angle:-90,position:"insideLeft",offset:14,fill:T.textDim,fontSize:10}}/>
          <ZAxis dataKey="z" range={[80,700]}/>
          <ReferenceLine x={0} stroke={T.textLight} strokeDasharray="3 3"/>
          <ReferenceLine y={0} stroke={T.textLight} strokeDasharray="3 3"/>
          <Tooltip content={({active,payload})=>{if(!active||!payload?.length)return null;const d=payload[0].payload;return(<div style={{background:"rgba(255,255,255,0.97)",border:`1px solid ${T.borderMed}`,borderRadius:8,padding:10,fontSize:11,color:T.text,boxShadow:T.shadowMd}}><div style={{color:d.fill,fontWeight:700}}>{d.name}</div><div>M1: <span style={{color:growthColor(d.x)}}>{fmtPctSign(d.x)}</span></div><div>M2: <span style={{color:growthColor(d.y)}}>{fmtPctSign(d.y)}</span></div><div>Vol: {d.z?.toLocaleString?.()}</div></div>);}} cursor={{stroke:T.accent,strokeDasharray:"3 3"}}/>
          <Scatter data={data} onClick={d=>onSelect&&onSelect(d.name)}>
            {data.map((d,i)=><Cell key={i} fill={d.fill} fillOpacity={selected&&selected!==d.name?0.2:0.7} stroke={selected===d.name?T.text:d.fill} strokeWidth={selected===d.name?2:1}/>)}
            <LabelList dataKey="name" position="top" style={{fill:T.textMid,fontSize:9}}/>
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

function LeagueTable({ data, cbos, metric, metricLabel, onSelect, selected }) {
  const sorted=[...cbos].sort((a,b)=>b[metric]-a[metric]);
  const top=sorted.slice(0,5), bot=sorted.slice(-5).reverse();
  const isGrowth=sorted.some(c=>Math.abs(c[metric])<5);
  const COLORS_L=[T.accent,"#1A7A9A",T.pos,T.warn,"#7A3A8A","#800010"];
  const groups=[...new Set(cbos.map(c=>c.l2))];
  const cm={};
  groups.forEach((g,i)=>{cm[g]=REGION_COLOR[g]||COLORS_L[i%COLORS_L.length];});
  const Row=({rank,c,isTop})=>(<div onClick={()=>onSelect&&onSelect(c.l3)} style={{display:"grid",gridTemplateColumns:"18px 1fr 70px 70px",alignItems:"center",gap:8,padding:"6px 6px",background:selected===c.l3?T.accentSoft:"transparent",borderLeft:`2px solid ${cm[c.l2]||T.accent}`,cursor:"pointer",borderRadius:3}}><div style={{color:isTop?T.pos:T.neg,fontSize:10,fontWeight:700,textAlign:"center"}}>{rank}</div><div><div style={{color:T.text,fontSize:11,fontWeight:500}}>{c.l3}</div><div style={{color:T.textDim,fontSize:9}}>{c.vol>1000?(c.vol/1000).toFixed(1)+"K":c.vol?.toLocaleString?.()}</div></div><div style={{color:isGrowth?growthColor(c[metric]):T.text,fontSize:11,fontWeight:700,textAlign:"right"}}>{isGrowth?fmtPctSign(c[metric]):c[metric]?.toLocaleString?.()??"-"}</div><div style={{height:22}}>{(data.momData[c.l3]||[]).filter(v=>v!==null).length>0&&<Sparkline data={data.momData[c.l3]||[]} color={isTop?T.pos:T.neg} height={22}/>}</div></div>);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div><div style={{color:T.pos,fontSize:9,letterSpacing:1.5,fontWeight:700,marginBottom:4,textTransform:"uppercase"}}>▲ TOP 5 — {metricLabel}</div>{top.map((c,i)=><Row key={c.l3} rank={i+1} c={c} isTop/>)}</div>
      <div style={{height:1,background:T.border}}/>
      <div><div style={{color:T.neg,fontSize:9,letterSpacing:1.5,fontWeight:700,marginBottom:4,textTransform:"uppercase"}}>▼ BOTTOM 5 — {metricLabel}</div>{bot.map((c,i)=><Row key={c.l3} rank={i+1} c={c} isTop={false}/>)}</div>
    </div>
  );
}

function CyclingVsGrowth({ cbos }) {
  const data=[...cbos].sort((a,b)=>a.ytdCyc-b.ytdCyc).map(c=>({name:c.l3,cyc:c.ytdCyc,ytd:c.ytdGr,risk:c.ytdCyc<-0.05&&c.ytdGr<0}));
  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{top:14,right:14,bottom:36,left:-4}}>
        <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false}/>
        <XAxis dataKey="name" tick={{fill:T.textMid,fontSize:9.5}} axisLine={{stroke:T.border}} tickLine={false} angle={-32} textAnchor="end" interval={0} height={56}/>
        <YAxis yAxisId="L" tickFormatter={v=>`${(v*100).toFixed(0)}%`} tick={{fill:T.accent,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/>
        <YAxis yAxisId="R" orientation="right" tickFormatter={v=>`${(v*100).toFixed(0)}%`} tick={{fill:T.warn,fontSize:10}} axisLine={{stroke:T.border}} tickLine={false}/>
        <Tooltip content={({active,payload})=>{if(!active||!payload?.length)return null;const d=payload[0].payload;return(<div style={{background:"rgba(255,255,255,0.97)",border:`1px solid ${T.borderMed}`,borderRadius:8,padding:10,fontSize:11,color:T.text,boxShadow:T.shadowMd}}><div style={{fontWeight:700}}>{d.name}</div><div>Cycling: <span style={{color:T.accent}}>{fmtPctSign(d.cyc)}</span></div><div>YTD Gr: <span style={{color:T.warn}}>{fmtPctSign(d.ytd)}</span></div>{d.risk&&<div style={{color:T.neg,fontSize:10,marginTop:4}}>⚠ Declining on easy comp</div>}</div>);}} cursor={{fill:`${T.accent}08`}}/>
        <ReferenceLine yAxisId="L" y={0} stroke={T.textLight} strokeDasharray="3 3"/>
        <Bar yAxisId="L" dataKey="cyc" name="YTD Cycling" radius={[3,3,3,3]}>{data.map((d,i)=><Cell key={i} fill={d.risk?T.neg:T.accent} fillOpacity={d.risk?0.85:0.5}/>)}</Bar>
        <Line yAxisId="R" type="monotone" dataKey="ytd" name="YTD Growth" stroke={T.warn} strokeWidth={2.2} dot={{r:3.5,fill:T.warn,strokeWidth:0}} activeDot={{r:5.5}}/>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function CategorySmallMultiples({ data, selectedCbos }) {
  const buildSeries=cat=>data.months.map((m,i)=>{
    const cbosInScope=selectedCbos.length>0?selectedCbos:Object.keys(data.momCat);
    let sumWV=0,sumW=0;
    cbosInScope.forEach(k=>{if(!data.momCat[k])return;const v=data.momCat[k][cat]?.[i];const wRow=data.catData.find(r=>r.l3===k&&r.cat===cat);if(v!=null&&wRow){sumWV+=v*wRow.vol;sumW+=wRow.vol;}});
    return{m:String(m).slice(0,3)+String(m).slice(-3),v:sumW>0?sumWV/sumW:null};
  });
  const allVals=CATEGORIES.flatMap(c=>buildSeries(c).map(d=>d.v).filter(v=>v!=null));
  const yMin=Math.min(-0.2,...allVals)-0.05, yMax=Math.max(0.5,...allVals)+0.05;
  if (!allVals.length) return <div style={{color:T.textDim,textAlign:"center",padding:30,fontSize:13}}>No category trend data</div>;
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,height:"100%"}}>
      {CATEGORIES.map(cat=>{
        const series=buildSeries(cat), last=series[series.length-1]?.v;
        return (<div key={cat} style={{border:`1px solid ${T.border}`,borderRadius:8,padding:8,background:`${CAT_COLOR[cat]}06`}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}><span style={{color:CAT_COLOR[cat],fontSize:11,fontWeight:700}}>● {cat}</span><span style={{color:growthColor(last),fontSize:11,fontWeight:700}}>{fmtPctSign(last)}</span></div>
          <ResponsiveContainer width="100%" height={100}>
            <AreaChart data={series} margin={{top:4,right:2,bottom:0,left:-22}}>
              <defs><linearGradient id={`gc-${cat.replace(/\s/g,"")}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={CAT_COLOR[cat]} stopOpacity={0.4}/><stop offset="100%" stopColor={CAT_COLOR[cat]} stopOpacity={0}/></linearGradient></defs>
              <YAxis domain={[yMin,yMax]} hide/>
              <XAxis dataKey="m" tick={{fill:T.textDim,fontSize:8}} axisLine={false} tickLine={false} interval={Math.max(1,Math.floor(data.months.length/6))}/>
              <ReferenceLine y={0} stroke={T.textLight} strokeDasharray="2 2"/>
              <Tooltip content={({active,payload,label})=>active&&payload?.length?(<div style={{background:"rgba(255,255,255,0.97)",border:`1px solid ${T.borderMed}`,borderRadius:6,padding:"5px 9px",fontSize:10,color:T.text}}><span style={{color:T.textDim,marginRight:6}}>{label}</span><span style={{color:growthColor(payload[0].value)}}>{fmtPctSign(payload[0].value)}</span></div>):null}/>
              <Area type="monotone" dataKey="v" stroke={CAT_COLOR[cat]} strokeWidth={1.6} fill={`url(#gc-${cat.replace(/\s/g,"")})`}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>);
      })}
    </div>
  );
}

// ── Insights builder + card ────────────────────────────────────────────────
function buildInsights(data) {
  if (!data.isHCCB) {
    const cbos=data.cboData, sorted=[...cbos].sort((a,b)=>b.ytdGr-a.ytdGr);
    const topGrower=sorted[0], bottomGrower=sorted[sorted.length-1];
    const avgGr=cbos.reduce((s,c)=>s+(c.ytdGr||0),0)/(cbos.length||1);
    const ins=[];
    if(topGrower) ins.push({icon:"⭐",title:`Top Performer: ${topGrower.l3}`,priority:"STANDOUT",narrative:`${topGrower.l3} leads with ${fmtPctSign(topGrower.ytdGr)}. ${((topGrower.ytdGr-avgGr)*100).toFixed(1)}pp above portfolio average of ${fmtPctSign(avgGr)}.`,actions:["Analyse outperformance drivers","Identify replicable factors","Share playbook across teams"],color:T.pos});
    if(bottomGrower&&bottomGrower!==topGrower) ins.push({icon:"⚠️",title:`Attention Needed: ${bottomGrower.l3}`,priority:"CRITICAL",narrative:`${bottomGrower.l3} at ${fmtPctSign(bottomGrower.ytdGr)} is the weakest performer, ${Math.abs((bottomGrower.ytdGr-avgGr)*100).toFixed(1)}pp below average.`,actions:["Root cause analysis","Compare to top performer","Set 30-day improvement targets"],color:T.neg});
    ins.push({icon:"📊",title:"Portfolio Overview",priority:"CONTEXT",narrative:`Portfolio averages ${fmtPctSign(avgGr)} across ${cbos.length} segments. ${cbos.filter(c=>c.ytdGr>avgGr).length} segments above average.`,actions:["Focus investment on below-average segments","Set targets 2pp above current average"],color:T.accent});
    return ins;
  }
  const out=[], cbos=data.cboData;
  const risk=cbos.filter(c=>c.ytdCyc<-0.05&&c.ytdGr<0).sort((a,b)=>a.ytdGr-b.ytdGr);
  if(risk.length>0){const r=risk[0];out.push({icon:"🚨",title:"Critical: Compounding Decline",priority:"CRITICAL",narrative:`${r.l3} declining ${fmtPctSign(r.ytdGr)} YTD despite an easy ${fmtPctSign(r.ytdCyc)} base. This is structural, not cyclical.`,actions:[`Deploy SWAT team to ${r.l3}`,"Channel audit + competitor pricing","Weekly volume tracking"],color:T.neg});}
  const sustained=cbos.filter(c=>c.cagr2y>0.08&&c.ytdGr>0.05&&c.ytdCyc>0.05).sort((a,b)=>b.cagr2y-a.cagr2y);
  if(sustained.length>0){const s=sustained[0];out.push({icon:"⭐",title:"Sustained Growth Leader",priority:"REPLICATE",narrative:`${s.l3} compounding at ${fmtPctSign(s.cagr2y)} 2Y CAGR with ${fmtPctSign(s.ytdGr)} YTD on a tough ${fmtPctSign(s.ytdCyc)} base.`,actions:[`Codify the ${s.l3} playbook`,"Map RTM + activation differences","Pilot in lowest-CAGR CBO"],color:T.pos});}
  const regionPerf=REGIONS.map(r=>{const rc=cbos.filter(c=>c.l2===r);return{r,ytd:aggGrowth(rc,"ytdGr"),vol:rc.reduce((s,c)=>s+c.vol,0)};}).filter(x=>x.vol>0);
  const sortedR=regionPerf.sort((a,b)=>a.ytd-b.ytd), weakest=sortedR[0], strongest=sortedR[sortedR.length-1];
  if(weakest&&strongest&&strongest.ytd-weakest.ytd>0.03) out.push({icon:"🎯",title:"Regional Performance Gap",priority:"STRATEGIC",narrative:`${strongest.r} at ${fmtPctSign(strongest.ytd)} vs ${weakest.r} at ${fmtPctSign(weakest.ytd)} — a ${((strongest.ytd-weakest.ytd)*100).toFixed(1)}pp spread.`,actions:[`Reallocate marketing toward ${weakest.r}`,"Cross-pollinate talent","Set region-specific quarterly targets"],color:T.accent});
  if(out.length===0) out.push({icon:"📊",title:"Portfolio Performing",priority:"CONTEXT",narrative:`${cbos.length} territories tracked. Portfolio YTD at ${fmtPctSign(aggGrowth(cbos,"ytdGr"))}. No structural anomalies detected.`,actions:["Continue monitoring cycling metrics","Review MTD for early signals"],color:T.accent});
  return out;
}

function InsightCard({ insight }) {
  return (
    <div style={{background:`linear-gradient(140deg,${insight.color}0d 0%,${T.surface} 100%)`,border:`1px solid ${insight.color}33`,borderLeft:`3px solid ${insight.color}`,borderRadius:10,padding:16,minHeight:200,display:"flex",flexDirection:"column",minWidth:300,boxShadow:T.shadow}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}><span style={{fontSize:22}}>{insight.icon}</span><span style={{fontSize:9,fontWeight:700,letterSpacing:1.2,color:insight.color,background:`${insight.color}18`,padding:"2px 8px",borderRadius:4}}>{insight.priority}</span></div>
      <div style={{color:T.text,fontSize:14,fontWeight:700,marginBottom:8,lineHeight:1.25}}>{insight.title}</div>
      <div style={{color:T.textMid,fontSize:11.5,lineHeight:1.55,marginBottom:12,flex:1}}>{insight.narrative}</div>
      <div style={{borderTop:`1px solid ${T.border}`,paddingTop:10}}>
        <div style={{color:T.textDim,fontSize:8.5,letterSpacing:1.5,fontWeight:700,marginBottom:5,textTransform:"uppercase"}}>Recommended Actions</div>
        {insight.actions.map((a,i)=><div key={i} style={{display:"flex",gap:6,fontSize:10.5,color:T.textMid,marginBottom:3}}><span style={{color:insight.color}}>▸</span><span>{a}</span></div>)}
      </div>
    </div>
  );
}

// ── Filter Bar ─────────────────────────────────────────────────────────────
function FilterBar({ data, regionSel, setRegionSel, cboSel, setCboSel, catSel, setCatSel, timePeriod, setTimePeriod, monthIdx, setMonthIdx, onReset, onOpenChat }) {
  const toggle=(val,list,setter)=>list.includes(val)?setter(list.filter(x=>x!==val)):setter([...list,val]);
  const filteredCbos=data.cboData.filter(c=>regionSel.length===0||regionSel.includes(c.l2));
  const regions=[...new Set(data.cboData.map(c=>c.l2).filter(Boolean))];
  const COLORS_L=[T.accent,"#1A7A9A",T.pos,T.warn,"#7A3A8A","#800010"];
  const cm={};
  regions.forEach((r,i)=>{cm[r]=REGION_COLOR[r]||COLORS_L[i%COLORS_L.length];});
  return (
    <div style={{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:"12px 20px",position:"sticky",top:0,zIndex:50,boxShadow:"0 2px 8px rgba(232,0,26,0.06)"}}>
      <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:14}}>
        {regions.length>1&&(
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <span style={{color:T.textDim,fontSize:9.5,letterSpacing:1.2,textTransform:"uppercase",fontWeight:700}}>{data.columnLabels?.groupCol||"Group"}</span>
            {regions.map(r=>{const on=regionSel.includes(r)||regionSel.length===0;return(<button key={r} onClick={()=>toggle(r,regionSel,setRegionSel)} style={{background:on?`${cm[r]}22`:"transparent",border:`1px solid ${on?cm[r]:T.border}`,color:on?T.text:T.textDim,padding:"4px 10px",borderRadius:999,fontSize:10.5,fontWeight:600,cursor:"pointer",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{String(r).length>22?String(r).slice(0,20)+"…":r}</button>);})}
          </div>
        )}
        {data.isHCCB&&(<>
          <div style={{width:1,height:20,background:T.border}}/>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{color:T.textDim,fontSize:9.5,letterSpacing:1.2,textTransform:"uppercase",fontWeight:700}}>Category</span>
            {CATEGORIES.map(c=>{const on=catSel.includes(c)||catSel.length===0;const icons={Sparkling:"🫧",Water:"💧",Juice:"🍊","All Others":"✦"};return(<button key={c} onClick={()=>toggle(c,catSel,setCatSel)} style={{background:on?`${CAT_COLOR[c]}20`:"transparent",border:`1px solid ${on?CAT_COLOR[c]:T.border}`,color:on?T.text:T.textDim,padding:"4px 9px",borderRadius:999,fontSize:10.5,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}><span>{icons[c]}</span>{c}</button>);})}
          </div>
        </>)}
        <div style={{width:1,height:20,background:T.border}}/>
        <div style={{display:"flex",gap:0,border:`1px solid ${T.border}`,borderRadius:6,overflow:"hidden"}}>
          {["MTD","QTD","YTD","CAGR"].map(p=><button key={p} onClick={()=>setTimePeriod(p)} style={{background:timePeriod===p?T.accent:"transparent",color:timePeriod===p?"#fff":T.textMid,padding:"4px 10px",fontSize:10.5,fontWeight:700,cursor:"pointer",border:"none"}}>{p}</button>)}
        </div>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
          {data.months.length>0&&(<>
            <span style={{color:T.textDim,fontSize:9.5}}>Period:</span>
            <select value={monthIdx} onChange={e=>setMonthIdx(Number(e.target.value))} style={{background:T.surface,color:T.text,border:`1px solid ${T.border}`,borderRadius:6,padding:"3px 6px",fontSize:10.5,cursor:"pointer",maxWidth:140}}>
              {data.months.map((m,i)=><option key={String(m)+i} value={i}>{String(m)}</option>)}
            </select>
          </>)}
          {onOpenChat&&<button onClick={onOpenChat} style={{background:T.accent,color:"#fff",border:"none",padding:"6px 14px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>🤖 Ask AI</button>}
          {onReset&&<button onClick={onReset} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textMid,padding:"4px 10px",borderRadius:6,fontSize:9.5,fontWeight:700,cursor:"pointer",letterSpacing:0.5}}>↻ NEW FILE</button>}
        </div>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",marginTop:8,gap:5}}>
        <span style={{color:T.textDim,fontSize:9.5,letterSpacing:1.2,textTransform:"uppercase",fontWeight:700,marginRight:4}}>{data.columnLabels?.segCol||"Segment"}</span>
        {filteredCbos.slice(0,40).map(c=>{const on=cboSel.includes(c.l3)||cboSel.length===0;return(<button key={c.l3} onClick={()=>toggle(c.l3,cboSel,setCboSel)} style={{background:on?`${cm[c.l2]||T.accent}18`:"transparent",border:`1px solid ${on?`${cm[c.l2]||T.accent}70`:T.border}`,color:on?T.text:T.textDim,padding:"2px 8px",borderRadius:4,fontSize:9.5,fontWeight:600,cursor:"pointer"}}>{c.l3}</button>);})}
        {filteredCbos.length>40&&<span style={{color:T.textDim,fontSize:9.5,fontStyle:"italic"}}>+{filteredCbos.length-40} more</span>}
        {cboSel.length>0&&<button onClick={()=>setCboSel([])} style={{color:T.accent,fontSize:9.5,background:"none",border:"none",cursor:"pointer"}}>↻ Reset</button>}
      </div>
    </div>
  );
}

// ── Main Dashboard Export ──────────────────────────────────────────────────
export default function Dashboard({ session, data, onReset, fileName, onAskAI }) {
  // Shared edit/version state (used by HCCB + LLM both)
  const [showEdit,    setShowEdit]    = useState(false);
  const [specHistory, setSpecHistory] = useState([]);
  const [spec,        setSpec]        = useState(null);
  const [rows,        setRows]        = useState([]);

  // HCCB filter/drill states
  const [regionSel,  setRegionSel]  = useState([]);
  const [cboSel,     setCboSel]     = useState([]);
  const [catSel,     setCatSel]     = useState([]);
  const [timePeriod, setTimePeriod] = useState("YTD");
  const [monthIdx,   setMonthIdx]   = useState(Math.max(0,(data?.months?.length||1)-1));
  const [drillCbo,   setDrillCbo]   = useState(null);
  const defaultTrendCbos=useMemo(()=>[...(data?.cboData||[])].sort((a,b)=>b.vol-a.vol).slice(0,4).map(c=>c.l3),[data]);
  const [trendCbos,  setTrendCbos]  = useState(defaultTrendCbos);
  const colLabels=data?.columnLabels||{};

  useEffect(()=>{setTrendCbos(defaultTrendCbos);setMonthIdx(Math.max(0,(data?.months?.length||1)-1));setRegionSel([]);setCboSel([]);setCatSel([]);setDrillCbo(null);},[data,defaultTrendCbos]);

  const filteredCbos=useMemo(()=>(data?.cboData||[]).filter(c=>(regionSel.length===0||regionSel.includes(c.l2))&&(cboSel.length===0||cboSel.includes(c.l3))),[data,regionSel,cboSel]);
  const filteredCats=useMemo(()=>{const cboNames=new Set(filteredCbos.map(c=>c.l3));return(data?.catData||[]).filter(r=>cboNames.has(r.l3)&&(catSel.length===0||catSel.includes(r.cat)));},[data,filteredCbos,catSel]);
console.log("Filtered CBOs:",filteredCbos);
  const kpis=useMemo(()=>{
    const totalVol=filteredCbos.reduce((s,c)=>s+(c.vol||0),0);
    return{totalVol,ytdGr:aggGrowth(filteredCbos,"ytdGr"),qtdGr:aggGrowth(filteredCbos,"qtdGr"),mtdGr:aggGrowth(filteredCbos,"mtdGr"),cagr2y:agg2YCAGR(filteredCbos,"cagr2y"),ytdCyc:aggCycling(filteredCbos,"ytdGr","ytdCyc")};
  },[filteredCbos]);

  const metricKey   = timePeriod==="MTD"?"mtdGr":timePeriod==="QTD"?"qtdGr":timePeriod==="CAGR"?"cagr2y":"ytdGr";
  const metricLabel = timePeriod==="CAGR"?"2Y CAGR":`${timePeriod} Growth`;
  const metricAvg   = timePeriod==="MTD"?kpis.mtdGr:timePeriod==="QTD"?kpis.qtdGr:timePeriod==="CAGR"?kpis.cagr2y:kpis.ytdGr;
  const trendKeys   = useMemo(()=>{const allow=new Set(filteredCbos.map(c=>c.l3));const f=trendCbos.filter(n=>allow.has(n));return f.length>0?f.slice(0,5):defaultTrendCbos.filter(n=>allow.has(n)).slice(0,4);},[trendCbos,filteredCbos,defaultTrendCbos]);
  const insights    = useMemo(()=>buildInsights({...data,cboData:filteredCbos,catData:filteredCats}),[data,filteredCbos,filteredCats]);
  const totalPortfolioVol=(data?.cboData||[]).reduce((s,c)=>s+c.vol,0)||1;

  const handleHCCBUpdate=useCallback(async(newSpec)=>{
    setSpecHistory(prev=>[...prev,spec]);
    setSpec(newSpec);
    const vNum=specHistory.length+2;
    if(session?.session_id) await saveDashboardVersion(session.session_id,newSpec,`Version ${vNum}`);
  },[spec,specHistory,session]);

  if (!data) return null;
if (data.isDDR) {
    return <DDRDashboard data={data} session={session} onAskAI={onAskAI} onReset={onReset} />;
  }
  return (
    <div style={{minHeight:"100%",background:T.bgAlt,color:T.text,paddingBottom:40}}>
      <style>{`
        @keyframes dashSpin    { to { transform: rotate(360deg); } }
        @keyframes dashFadeSlide { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes dashSlideIn  { from{transform:translateX(100%)} to{transform:translateX(0)} }
        @keyframes dashWave     { 0%,100%{transform:scaleY(0.35);opacity:0.5} 50%{transform:scaleY(1);opacity:1} }
        @keyframes dashFadeIn   { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes dashBarGrow  { 0%,100%{transform:scaleY(0.4)} 50%{transform:scaleY(1)} }
        @keyframes dashFadeUp   { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* HCCB header + filter bar */}
      {data.isHCCB&&(<>
        <div style={{background:T.accent,padding:"14px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:T.shadowMd}}>
          <div>
            <div style={{color:"#fff",fontWeight:700,fontSize:18,letterSpacing:-0.5}}>Volume & Growth Command Center</div>
            <div style={{color:"rgba(255,255,255,0.7)",fontSize:10.5,marginTop:2,letterSpacing:0.5}}>CEO & CFO Market Intelligence · {data.cboData.length} segments · {data.months.length} periods</div>
          </div>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            {specHistory.length>0&&(
              <VersionHistoryDropdown specHistory={specHistory} currentSpec={spec} sessionId={session?.session_id}
                onRestore={(s,fromSaved)=>{if(fromSaved)setSpec(s);else{const t=specHistory[s];setSpecHistory(h=>h.slice(0,s));setSpec(t);}}}
              />
            )}
            {data.months.length>0&&(<div style={{textAlign:"right"}}><div style={{color:"rgba(255,255,255,0.6)",fontSize:9,letterSpacing:1.5,textTransform:"uppercase"}}>Latest</div><div style={{color:"#fff",fontSize:13,fontWeight:700}}>{String(data.months[data.months.length-1])}</div></div>)}
            <button onClick={()=>setShowEdit(p=>!p)} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",color:"#fff",padding:"7px 14px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
              ✏️ {showEdit?"Editing…":"Edit Dashboard"}
            </button>
          </div>
        </div>
        <div style={{height:3,background:`linear-gradient(90deg,${T.accentDeep},${T.accent},${T.accentDeep})`}}/>
        <FilterBar data={data} regionSel={regionSel} setRegionSel={setRegionSel} cboSel={cboSel} setCboSel={setCboSel} catSel={catSel} setCatSel={setCatSel} timePeriod={timePeriod} setTimePeriod={setTimePeriod} monthIdx={monthIdx} setMonthIdx={setMonthIdx} onReset={onReset} onOpenChat={onAskAI}/>
      </>)}

      {/* HCCB body or LLM dashboard */}
      {data.isHCCB?(
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:14,paddingRight:showEdit?400:16}}>
          {/* KPIs */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
            <KPICard label={colLabels.vol||"Total Volume"} value={fmtVolMn(kpis.totalVol)} subtitle={`${filteredCbos.length} segments · ${fmtPct(kpis.totalVol/totalPortfolioVol)} of total`} color={T.accent} sparkData={data.momData["HCCB+ Total"]} sparkColor={T.accent} icon="📦"/>
            <KPICard label={colLabels.ytdGr||"YTD Growth"} value={fmtPctSign(kpis.ytdGr)} delta={kpis.ytdGr-kpis.cagr2y} color={growthColor(kpis.ytdGr)} icon="📈"/>
            <KPICard label={colLabels.mtdGr||"MTD Growth"} value={fmtPctSign(kpis.mtdGr)} delta={kpis.mtdGr-kpis.ytdGr} color={growthColor(kpis.mtdGr)} subtitle="vs YTD" icon="🗓"/>
            <KPICard label={colLabels.qtdGr||"QTD Growth"} value={fmtPctSign(kpis.qtdGr)} delta={kpis.qtdGr-kpis.ytdGr} color={growthColor(kpis.qtdGr)} icon="📊"/>
            <KPICard label={colLabels.cagr2y||"2Y CAGR"} value={fmtPctSign(kpis.cagr2y)} color={growthColor(kpis.cagr2y)} subtitle="Structural pace" icon="🎯"/>
          </div>

          {/* Heatmap + Category donut */}
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:14}}>
            <Card title="Territory Performance Heatmap" subtitle="Click any row to drill" badge="HCCB+">
              <Heatmap cbos={filteredCbos} onSelect={n=>setDrillCbo(drillCbo===n?null:n)} selected={drillCbo}/>
            </Card>
            {data.catData.length>0
              ?<Card title="Category Mix" subtitle="Volume share + YTD growth"><CategoryDonut rows={filteredCats}/></Card>
              :<Card title={`${colLabels.vol||"Volume"} Distribution`} subtitle="Top segments"><GenericPie cbos={filteredCbos}/></Card>
            }
          </div>

          {/* MoM trend */}
          <Card title={data.isHCCB?"Month-over-Month Growth Trend":"Time Series Trend"} subtitle="Select up to 5 segments · toggle below" badge={data.months.length>0?`${data.months.length} periods`:""}>
            <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>
              {filteredCbos.slice(0,30).map(c=>{
                const on=trendKeys.includes(c.l3);
                const COLORS_L=[T.accent,"#1A7A9A",T.pos,T.warn,"#7A3A8A","#800010"];
                const groups=[...new Set(data.cboData.map(x=>x.l2))];
                const cm2={};
                groups.forEach((g,i)=>{cm2[g]=REGION_COLOR[g]||COLORS_L[i%COLORS_L.length];});
                return(<button key={c.l3} onClick={()=>{if(on)setTrendCbos(trendKeys.filter(x=>x!==c.l3));else if(trendKeys.length<5)setTrendCbos([...trendKeys,c.l3]);}} style={{background:on?`${cm2[c.l2]||T.accent}22`:"transparent",border:`1px solid ${on?cm2[c.l2]||T.accent:T.border}`,color:on?T.text:T.textDim,padding:"2px 8px",borderRadius:4,fontSize:9.5,fontWeight:600,cursor:"pointer",opacity:!on&&trendKeys.length>=5?0.4:1}}>{c.l3}</button>);
              })}
            </div>
            {data.isHCCB?<MoMTrend data={data} selectedKeys={trendKeys}/>:<GenericTrend data={data} selectedKeys={trendKeys}/>}
          </Card>

          {/* Ranking + Quadrant */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <Card title={`${metricLabel} Ranking`} subtitle="Sorted · dashed = portfolio avg">
              <CBOWaterfall cbos={filteredCbos} metric={metricKey} avgVal={metricAvg} onSelect={n=>setDrillCbo(drillCbo===n?null:n)} selected={drillCbo}/>
            </Card>
            <Card title="Strategic Quadrant" subtitle="Metric 1 × Metric 2 · bubble = volume">
              <QuadrantBubble cbos={filteredCbos} onSelect={n=>setDrillCbo(drillCbo===n?null:n)} selected={drillCbo}/>
            </Card>
          </div>

          {/* Cycling vs Growth + League Table */}
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:14}}>
            <Card title="Base Difficulty vs Performance" subtitle="Bars = YTD cycling · line = YTD growth">
              <CyclingVsGrowth cbos={filteredCbos}/>
            </Card>
            <Card title={`League Table — ${metricLabel}`} subtitle="Top & Bottom 5 with sparklines">
              <LeagueTable data={data} cbos={filteredCbos} metric={metricKey} metricLabel={metricLabel} onSelect={n=>setDrillCbo(drillCbo===n?null:n)} selected={drillCbo}/>
            </Card>
          </div>

          {/* Category small multiples */}
          {data.catData.length>0&&(
            <Card title="Category Momentum — Monthly Trend" subtitle="MoM growth per category, volume-weighted" style={{minHeight:320}}>
              <CategorySmallMultiples data={data} selectedCbos={filteredCbos.map(c=>c.l3)}/>
            </Card>
          )}

          {/* Generic data table for non-HCCB shown as HCCB */}
          {!data.isHCCB&&(
            <Card title={`${colLabels.segCol||"Segment"} Performance Table`}>
              <GenericDataTable cbos={filteredCbos} onSelect={n=>setDrillCbo(drillCbo===n?null:n)} selected={drillCbo} colLabels={colLabels}/>
            </Card>
          )}

          {/* AI Insights */}
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:18,boxShadow:T.shadow}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{color:T.text,fontSize:17,fontWeight:800,letterSpacing:-0.3}}><span style={{color:T.accent,marginRight:8}}>✦</span>AI-Generated Insights</div>
                <div style={{color:T.textDim,fontSize:10.5,marginTop:2}}>Auto-derived from current filter scope · {insights.length} insight{insights.length===1?"":"s"}</div>
              </div>
              {onAskAI&&<button onClick={onAskAI} style={{background:T.accent,color:"#fff",border:"none",padding:"7px 16px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer"}}>Ask AI More ↗</button>}
            </div>
            <div style={{display:"flex",gap:12,overflowX:"auto",paddingBottom:8}}>
              {insights.map((ins,i)=><InsightCard key={i} insight={ins}/>)}
            </div>
          </div>

          {/* HCCB Edit Panel */}
          {showEdit&&(
            <DashboardEditPanel
              spec={spec||{title:"HCCB+ Dashboard",kpis:[],charts:[],tables:[],insights:[],filters:[]}}
              rows={rows}
              onUpdate={handleHCCBUpdate}
              onClose={()=>setShowEdit(false)}
            />
          )}
        </div>
      ):(
        <LLMDashboard
          data={data} sessionId={session?.session_id}
          onAskAI={onAskAI} onReset={onReset}
          showEdit={showEdit} setShowEdit={setShowEdit}
          specHistory={specHistory} setSpecHistory={setSpecHistory}
          spec={spec} setSpec={setSpec}
          rows={rows} setRows={setRows}
        />
      )}
    </div>
  );
}